import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import * as workflowExecutionService from '../services/workflowExecutionService';
import * as personaService from '../services/personaService';
import * as phaseExecutionService from '../services/phaseExecutionService';
import * as sessionService from '../services/sessionService';
import { PhaseDependencyGraph } from '../components/sessions/PhaseDependencyGraph';
import { useWorkflowWebSocket } from '../hooks/useWebSocket';
import { InputReviewModal } from '../components/workflows/InputReviewModal';
import { CommitteeModeSelector } from '../components/committees/CommitteeModeSelector';
import { PhaseStatusIndicator } from '../components/committees/PhaseStatusIndicator';
import { CommitteeExecutionVisualizer } from '../components/committees/CommitteeExecutionVisualizer';
import { IntermediateArtifactsViewer } from '../components/committees/IntermediateArtifactsViewer';
import { AgentActivityTimeline } from '../components/committees/AgentActivityTimeline';
import { PollingIndicator } from '../components/workflows/PollingIndicator';
import RelatedExecutionsView from '../components/workflows/RelatedExecutionsView';

// Define WebSocket message types directly in this file
const WebSocketMessageType = {
  // Connection and session events
  CONNECTION_ESTABLISHED: "connection_established",
  SESSION_STATE: "session_state",
  SESSION_SAVED: "session_saved",

  // Phase events
  PHASE_UPDATE: "phase_update",
  PHASE_TRANSITION: "phase_transition",
  PHASE_COMPLETE: "phase_complete",
  PHASE_ACTION: "phase_action",

  // Committee review events
  COMMITTEE_REVIEW_STARTED: "committee_review_started",
  COMMITTEE_REVIEW_COMPLETED: "committee_review_completed",

  // Workflow execution events
  WORKFLOW_EXECUTION_STARTED: "workflow_execution_started",
  WORKFLOW_EXECUTION_UPDATED: "workflow_execution_updated",
  WORKFLOW_EXECUTION_PAUSED: "workflow_execution_paused",
  WORKFLOW_EXECUTION_RESUMED: "workflow_execution_resumed",
  WORKFLOW_EXECUTION_COMPLETED: "workflow_execution_completed",

  // Workflow phase events
  WORKFLOW_PHASE_STARTED: "workflow_phase_started",
  WORKFLOW_PHASE_UPDATED: "workflow_phase_updated",
  WORKFLOW_PHASE_COMPLETED: "workflow_phase_completed",
  WORKFLOW_PHASE_FAILED: "workflow_phase_failed",
  WORKFLOW_PHASE_SKIPPED: "workflow_phase_skipped",

  // Workflow agent events
  WORKFLOW_AGENT_ACTIVE: "workflow_agent_active",
  WORKFLOW_AGENT_IDLE: "workflow_agent_idle",
  WORKFLOW_AGENT_TOOL_USAGE: "workflow_agent_tool_usage",

  // Workflow artifact events
  WORKFLOW_ARTIFACT_CREATED: "workflow_artifact_created",
  WORKFLOW_ARTIFACT_UPDATED: "workflow_artifact_updated",

  // System events
  PING: "ping",
  PONG: "pong"
};

const WorkflowExecution = () => {
  // 1. React Router and Theme hooks
  const { executionId } = useParams<{ executionId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // 2. ALL state variables in one place
  const [execution, setExecution] = useState<any>(null);
  const [personas, setPersonas] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [agentActivities, setAgentActivities] = useState<any[]>([]);
  const [isWebSocketDisabled, setIsWebSocketDisabled] = useState(false);
  const [lastPollingTime, setLastPollingTime] = useState<Date | null>(null);
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);
  const [isRealTimeMode, setIsRealTimeMode] = useState(false);
  const [showInputReviewModal, setShowInputReviewModal] = useState(false);
  const [phaseInputs, setPhaseInputs] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [manualPersonaId, setManualPersonaId] = useState('');
  const [committeeMode, setCommitteeMode] = useState<string>('sequential');
  // We'll keep the same state variable but use it for both top-level and sub-tabs
  const [activeTab, setActiveTab] = useState<'workflow' | 'workflow_replay' | 'debug'>('workflow');
  const [workflowDefinition, setWorkflowDefinition] = useState<any>(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);
  const [committeeState, setCommitteeState] = useState({
    mode: 'sequential',
    currentPersonaIndex: -1,
    personas: [],
    iteration: 1,
    totalIterations: 1,
    phase: 'initial'
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);

  // 3. ALL refs in one place - initialize with default values that don't depend on hooks
  const fetchExecutionRef = useRef<() => Promise<void>>();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevConnectionStatusRef = useRef<string>('Closed'); // Use a default value that doesn't depend on connectionStatus
  const realTimeModeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTimeRef = useRef<number>(0);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const artifactsRef = useRef(new Map());

  // Clear artifacts map when executionId changes
  useEffect(() => {
    console.log('[ARTIFACTS] Clearing artifacts map due to execution ID change');
    artifactsRef.current.clear();
    setArtifacts([]);
  }, [executionId]);

  // Define the REFRESH_COOLDOWN constant
  const REFRESH_COOLDOWN = 2000; // 2 seconds

  // Helper function to ensure an artifact exists in our state
  const ensureArtifactExists = useCallback((artifactData, options = { forceUpdate: false }) => {
    if (!artifactData || !artifactData.id) {
      console.error('[ARTIFACT-MANAGER] Invalid artifact data:', artifactData);
      return false;
    }

    const artifactId = artifactData.id;
    const existingArtifact = artifactsRef.current.get(artifactId);

    // Log detailed debugging info
    console.log(`[ARTIFACT-MANAGER-DEBUG] Processing artifact ${artifactId}`);
    console.log(`[ARTIFACT-MANAGER-DEBUG] Existing in map: ${Boolean(existingArtifact)}`);
    console.log(`[ARTIFACT-MANAGER-DEBUG] Force update: ${Boolean(options.forceUpdate)}`);
    console.log(`[ARTIFACT-MANAGER-DEBUG] Current map size: ${artifactsRef.current.size}`);

    // Determine if we should update
    const shouldUpdate = !existingArtifact ||
                        options.forceUpdate ||
                        (!existingArtifact.content && artifactData.content);

    if (shouldUpdate) {
      console.log(`[ARTIFACT-MANAGER] ${existingArtifact ? 'Updating' : 'Adding new'} artifact ${artifactId}`);

      // Find the phase information
      const phaseId = artifactData.phase_id || (existingArtifact?.phase_id);
      const phase = execution?.phases.find(p => p.id === phaseId);
      const phaseLabel = artifactData.phase_label ||
                        phase?.label ||
                        existingArtifact?.phase_label ||
                        'Unknown';

      // Create a properly formatted artifact object with enhanced metadata
      const updatedArtifact = {
        ...artifactData,
        phase_id: phaseId,
        phase_label: phaseLabel,
        // Ensure persona information is preserved and enhanced
        persona_id: artifactData.persona_id || existingArtifact?.persona_id,
        persona_name: artifactData.persona_name || existingArtifact?.persona_name,
        // Add tags array if it doesn't exist
        tags: artifactData.tags || existingArtifact?.tags || [],
        _isNew: !existingArtifact,
        _contentFetched: Boolean(artifactData.content) || existingArtifact?._contentFetched
      };

      // Add phase as a tag if not already present
      if (phaseLabel && !updatedArtifact.tags.includes(phaseLabel)) {
        updatedArtifact.tags.push(phaseLabel);
      }

      // Add persona as a tag if not already present
      if (updatedArtifact.persona_id && !updatedArtifact.tags.includes(updatedArtifact.persona_id)) {
        updatedArtifact.tags.push(updatedArtifact.persona_id);
      }

      // Add special tags for synthesis artifacts
      if (artifactData.is_final_synthesis || artifactData.type === 'synthesis') {
        // Add 'Synthesis' tag if not already present
        if (!updatedArtifact.tags.includes('Synthesis')) {
          updatedArtifact.tags.push('Synthesis');
        }

        // Ensure the is_final_synthesis flag is set
        updatedArtifact.is_final_synthesis = true;

        // Remove redundant tags
        if (updatedArtifact.tags.includes('Final Synthesis')) {
          updatedArtifact.tags = updatedArtifact.tags.filter(tag => tag !== 'Final Synthesis');
        }

        // Remove lowercase 'synthesis' tag if present
        if (updatedArtifact.tags.includes('synthesis')) {
          updatedArtifact.tags = updatedArtifact.tags.filter(tag => tag !== 'synthesis');
        }
      }

      if (artifactData.is_review || artifactData.type === 'review') {
        if (!updatedArtifact.tags.includes('Review')) {
          updatedArtifact.tags.push('Review');
        }
        // Ensure the is_review flag is set
        updatedArtifact.is_review = true;
      }

      // Update our ref map
      artifactsRef.current.set(artifactId, updatedArtifact);
      console.log(`[ARTIFACT-MANAGER-DEBUG] Updated map, new size: ${artifactsRef.current.size}`);

      // Update React state (immutably)
      setArtifacts(prev => {
        // Create a new array with all artifacts except this one
        const filteredArtifacts = prev.filter(a => a.id !== artifactId);
        console.log(`[ARTIFACT-MANAGER-DEBUG] Filtered out existing artifact, remaining: ${filteredArtifacts.length}`);

        // Add the updated artifact
        const newArtifacts = [updatedArtifact, ...filteredArtifacts];

        // Sort by creation time (newest first)
        newArtifacts.sort((a, b) => {
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return timeB - timeA;
        });

        console.log(`[ARTIFACT-MANAGER] Updated artifacts state, now has ${newArtifacts.length} artifacts`);

        // Verify no duplicates
        const idSet = new Set();
        const hasDuplicates = newArtifacts.some(a => {
          if (idSet.has(a.id)) return true;
          idSet.add(a.id);
          return false;
        });

        if (hasDuplicates) {
          console.error(`[ARTIFACT-MANAGER-DEBUG] DUPLICATE DETECTED in artifacts array!`);
        } else {
          console.log(`[ARTIFACT-MANAGER-DEBUG] No duplicates in artifacts array, size: ${newArtifacts.length}`);
        }

        return newArtifacts;
      });

      // Update the phase's artifacts in execution state (only if it's a new artifact)
      if (!existingArtifact) {
        setExecution(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            phases: prev.phases.map((phase: any) =>
              phase.id === artifactData.phase_id
                ? {
                    ...phase,
                    artifacts: [...(phase.artifacts || []), artifactData]
                  }
                : phase
            )
          };
        });
      }

      // Trigger animation if it's a new artifact
      if (!existingArtifact) {
        setTimeout(() => {
          const artifactEvent = new CustomEvent('artifact-updated', {
            detail: { artifact: updatedArtifact, isNew: true }
          });
          window.dispatchEvent(artifactEvent);

          // Remove the _isNew flag after animation completes
          setTimeout(() => {
            setArtifacts(prev =>
              prev.map(a => a.id === artifactId ? {...a, _isNew: false} : a)
            );
          }, 3000);
        }, 100);
      }

      return true; // Artifact was added or updated
    }

    return false; // No change needed
  }, [execution]);

  // 4. WebSocket hook - add refs to track component mount state and WebSocket setup
  const isComponentMountedRef = useRef<boolean>(true);
  const websocketSetupRef = useRef<boolean>(false);
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const connectionIdRef = useRef<string>(`workflow-${executionId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  const { sendCommand, lastMessage, connectionStatus, reconnect } = useWorkflowWebSocket(
    executionId || '',
    isWebSocketDisabled || !isComponentMountedRef.current || execution?.status === 'completed',
    connectionIdRef.current
  );

  // 5. Effect to update isWebSocketDisabled based on execution
  useEffect(() => {
    if (execution) {
      setIsWebSocketDisabled(execution.status === 'completed');
    }
  }, [execution]);

  // Add more detailed logging for WebSocket status
  useEffect(() => {
    // Set the websocket setup flag to true to prevent duplicate connections
    if (!websocketSetupRef.current && connectionStatus === 'Open') {
      websocketSetupRef.current = true;
    }

    console.log(`[WEBSOCKET] Connection status changed: ${connectionStatus}`);

    // If connection is established, request the current execution state
    if (connectionStatus === 'Open' && executionId && isComponentMountedRef.current) {
      console.log('[WEBSOCKET] Connection established, requesting execution state');
      sendCommand('request_execution_state');

      // Set real-time mode to true when WebSocket connects
      console.log('[WEBSOCKET] Setting real-time mode to true because WebSocket is connected');
      setIsRealTimeMode(true);

      // Clear any existing polling interval (legacy cleanup)
      if (pollingIntervalRef.current) {
        console.log('[WEBSOCKET] Clearing polling interval ID:', pollingIntervalRef.current, 'because WebSocket is connected');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        console.log('[WEBSOCKET] Polling interval cleared, ref is now:', pollingIntervalRef.current);
      }
    } else if (connectionStatus === 'Error' || connectionStatus === 'Closed') {
      // When WebSocket disconnects, attempt to reconnect immediately
      console.log('[WEBSOCKET] Connection closed or error, attempting to reconnect immediately');

      // Reset the websocket setup flag when connection is closed or has error
      if (websocketSetupRef.current) {
        console.log('[WEBSOCKET] Resetting websocket setup flag to allow reconnection');
        websocketSetupRef.current = false;

        // Attempt to reconnect immediately
        reconnect();
      }
    }

    return () => {
      console.log('[WEBSOCKET] Cleanup from connection status effect');
    };
  }, [connectionStatus, executionId, sendCommand, reconnect]);

  // Add this useEffect to ensure polling is disabled when the component unmounts
  useEffect(() => {
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [errorTimeout]);
  
  // Helper function to get persona name from ID
  const getPersonaName = useCallback((personaId: string) => {
    if (!personaId) return 'Unknown';
    
    // If we have the persona data, use the name
    if (personas[personaId]?.name) {
      return personas[personaId].name;
    }
    
    // Otherwise, return the ID with a better format
    return personaId;
  }, [personas]);

  // Add this useEffect to ensure polling is disabled when the component unmounts
  useEffect(() => {
    console.log('[COMPONENT] Setting up event listeners and cleanup for execution:', executionId);

    // Set component mounted flag
    isComponentMountedRef.current = true;

    // Reset the websocket setup flag when the component mounts
    websocketSetupRef.current = false;

    // Reset the processed messages set
    processedMessagesRef.current.clear();

    // Log when artifacts state changes
    console.log('[ARTIFACTS] Initial artifacts count:', artifacts.length);

    // Add debug logging for execution and artifacts state changes
    const logExecutionState = () => {
      if (execution) {
        console.log('[EXECUTION-DEBUG] Execution state:', {
          id: execution.id,
          status: execution.status,
          current_phase_id: execution.current_phase_id,
          phases: execution.phases.map(p => ({
            id: p.id,
            status: p.status,
            artifacts: p.artifacts?.length || 0
          }))
        });
      }
    };

    // Log initial state
    logExecutionState();

    // Set up MutationObserver to detect DOM changes related to phase status
    const phaseStatusObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' &&
            mutation.attributeName === 'class' &&
            mutation.target.classList.contains('phase-status')) {
          console.log('[DOM-DEBUG] Phase status element changed:', mutation.target);
          logExecutionState();
        }
      });
    });

    // Observe phase status elements
    document.querySelectorAll('.phase-status').forEach(el => {
      phaseStatusObserver.observe(el, { attributes: true });
    });

    // Set up event listeners for artifact and agent activity updates
    const handleArtifactUpdated = (event: CustomEvent) => {
      if (!isComponentMountedRef.current) return;

      console.log('[EVENT] Artifact updated event received', event.detail);
      // Force re-render of artifact components with new references to ensure React detects changes
      setArtifacts(prev => prev.map(a => ({...a})));
    };

    const handleAgentActivityUpdated = (event: CustomEvent) => {
      if (!isComponentMountedRef.current) return;

      console.log('[EVENT] Agent activity updated event received', event.detail);
      // Force re-render of agent activity components with new references
      setAgentActivities(prev => prev.map(a => ({...a})));
    };

    // Add listener for phase execution events
    const handlePhaseExecutionStarted = (event: CustomEvent) => {
      if (!isComponentMountedRef.current) return;

      const { executionId: eventExecutionId, phaseId, committeeMode } = event.detail;

      // Only process events for this execution
      if (eventExecutionId !== executionId) return;

      console.log(`[EVENT] Phase execution started: ${phaseId} with mode ${committeeMode}`);

      // Enable real-time mode
      setIsRealTimeMode(true);

      // Force a refresh after a short delay, respecting cooldown
      setTimeout(() => {
        if (!isComponentMountedRef.current) return;

        if (fetchExecutionRef.current) {
          const now = Date.now();
          if (now - lastRefreshTimeRef.current >= REFRESH_COOLDOWN) {
            console.log('[EVENT] Forcing refresh after phase execution started');
            lastRefreshTimeRef.current = now;
            fetchExecutionRef.current();
          } else {
            console.log(`[EVENT] Skipping refresh after phase execution - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
          }
        }
      }, 1000);
    };

    const handlePhaseExecutionError = (event: CustomEvent) => {
      if (!isComponentMountedRef.current) return;

      const { executionId: eventExecutionId, phaseId, error } = event.detail;

      // Only process events for this execution
      if (eventExecutionId !== executionId) return;

      console.error(`[EVENT] Phase execution error: ${phaseId} - ${error}`);

      // Force a refresh to get the latest state, respecting cooldown
      if (fetchExecutionRef.current) {
        const now = Date.now();
        if (now - lastRefreshTimeRef.current >= REFRESH_COOLDOWN) {
          console.log('[EVENT] Forcing refresh after phase execution error');
          lastRefreshTimeRef.current = now;
          fetchExecutionRef.current();
        } else {
          console.log(`[EVENT] Skipping refresh after error - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
        }
      }
    };

    // Add global event listener for workflow messages
    const handleWorkflowMessage = (event: CustomEvent) => {
      if (!isComponentMountedRef.current) return;

      const { message, executionId: msgExecutionId } = event.detail;

      // Only process messages for this execution
      if (msgExecutionId !== executionId) return;

      console.log('[GLOBAL-EVENT] Workflow message received:', message.type);

      // Debounce the refresh to prevent multiple calls
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        if (!isComponentMountedRef.current) return;

        // Force refresh of data, respecting cooldown
        if (fetchExecutionRef.current) {
          const now = Date.now();
          if (now - lastRefreshTimeRef.current >= REFRESH_COOLDOWN) {
            console.log('[GLOBAL-EVENT] Forcing refresh after message:', message.type);
            lastRefreshTimeRef.current = now;
            fetchExecutionRef.current();
          } else {
            console.log(`[GLOBAL-EVENT] Skipping refresh after message - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
          }
        }
        refreshTimeoutRef.current = null;
      }, 500); // Debounce for 500ms

      // Set real-time mode to true
      setIsRealTimeMode(true);
    };

    window.addEventListener('artifact-updated', handleArtifactUpdated as EventListener);
    window.addEventListener('agent-activity-updated', handleAgentActivityUpdated as EventListener);
    window.addEventListener('phase-execution-started', handlePhaseExecutionStarted as EventListener);
    window.addEventListener('phase-execution-error', handlePhaseExecutionError as EventListener);
    window.addEventListener('workflow-message', handleWorkflowMessage as EventListener);

    // Set up a fallback mechanism that uses WebSocket reconnection instead of polling
    let fallbackIntervalId: NodeJS.Timeout | null = null;

    if (isRealTimeMode && executionId && isComponentMountedRef.current) {
      const lastUpdateTimeRef = { current: Date.now() };

      fallbackIntervalId = setInterval(() => {
        if (!isComponentMountedRef.current) {
          if (fallbackIntervalId) {
            clearInterval(fallbackIntervalId);
            fallbackIntervalId = null;
          }
          return;
        }

        const now = Date.now();
        if (now - lastUpdateTimeRef.current > 10000) { // 10 seconds
          console.log('[FALLBACK] No updates received for 10s, checking WebSocket status');

          // CRITICAL FIX: We can only check the connectionStatus since webSocketRef is not accessible here
          if (connectionStatus !== 'Open') {
            console.log('[FALLBACK] WebSocket not connected, attempting to reconnect');
            reconnect();
          } else {
            // Only if WebSocket is ACTUALLY open, request the latest state
            console.log('[FALLBACK] WebSocket connected, requesting latest state');
            // Use a try/catch to handle potential errors
            try {
              sendCommand('request_execution_state');
            } catch (error) {
              console.error('[FALLBACK] Error sending command:', error);
              // Force reconnect on error
              reconnect();
            }
          }

          lastUpdateTimeRef.current = now;
        }
      }, 10000);
    }

    return () => {
      console.log('[COMPONENT] Unmounting WorkflowExecution component, cleaning up resources');

      // Set component unmounted flag
      isComponentMountedRef.current = false;

      // Reset the websocket setup flag when the component unmounts
      websocketSetupRef.current = false;

      // Clear the processed messages set
      processedMessagesRef.current.clear();

      console.log('[POLLING-DEBUG] Component unmounting, ensuring polling is disabled');
      if (pollingIntervalRef.current) {
        console.log('[POLLING-DEBUG] Component unmount: Clearing interval ID:', pollingIntervalRef.current);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Clear any real-time mode timeout
      if (realTimeModeTimeoutRef.current) {
        clearTimeout(realTimeModeTimeoutRef.current);
        realTimeModeTimeoutRef.current = null;
      }

      // Clear refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      // Clear fallback interval if it exists
      if (fallbackIntervalId) {
        clearInterval(fallbackIntervalId);
      }

      // Remove event listeners
      window.removeEventListener('artifact-updated', handleArtifactUpdated as EventListener);
      window.removeEventListener('agent-activity-updated', handleAgentActivityUpdated as EventListener);
      window.removeEventListener('phase-execution-started', handlePhaseExecutionStarted as EventListener);
      window.removeEventListener('phase-execution-error', handlePhaseExecutionError as EventListener);
      window.removeEventListener('workflow-message', handleWorkflowMessage as EventListener);
    };
  }, [executionId, isRealTimeMode, REFRESH_COOLDOWN]);


  // Add logging for execution data
  useEffect(() => {
    if (execution) {
      console.log("Current execution data:", {
        id: execution.id,
        workflow_id: execution.workflow_id,
        phases: execution.phases?.length || 0,
        connections: execution.connections?.length || 0,
        current_phase_id: execution.current_phase_id,
        status: execution.status
      });
    }
  }, [execution]);

  // Add a handler for WebSocket messages that updates the cache
  const handleWebSocketMessage = useCallback((message) => {
    // When we receive a WebSocket message, invalidate the cache
    if (message.type.startsWith('workflow_') || message.type.startsWith('committee_')) {
      sessionService.invalidateExecutionCache(executionId);

      // ALWAYS ensure we're in real-time mode and polling is disabled
      if (!isRealTimeMode) {
        console.log('[WEBSOCKET] Setting real-time mode to true due to received message');
        setIsRealTimeMode(true);
      }

      // ALWAYS clear any polling interval when we receive a WebSocket message
      if (pollingIntervalRef.current) {
        console.log('[WEBSOCKET] Clearing polling interval due to WebSocket message:', pollingIntervalRef.current);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Debounce the refresh to prevent multiple calls
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Check if we're within the cooldown period
      const now = Date.now();
      if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN) {
        console.log(`[WEBSOCKET] Skipping immediate refresh - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);

        refreshTimeoutRef.current = setTimeout(() => {
          console.log('[WEBSOCKET] Debounced refresh after message:', message.type);
          if (fetchExecutionRef.current) {
            lastRefreshTimeRef.current = Date.now();
            fetchExecutionRef.current();
          }
          refreshTimeoutRef.current = null;
        }, REFRESH_COOLDOWN - (now - lastRefreshTimeRef.current));
      } else {
        // If outside cooldown period, refresh immediately
        refreshTimeoutRef.current = setTimeout(() => {
          console.log('[WEBSOCKET] Debounced refresh after message:', message.type);
          if (fetchExecutionRef.current) {
            lastRefreshTimeRef.current = Date.now();
            fetchExecutionRef.current();
          }
          refreshTimeoutRef.current = null;
        }, 100); // Small delay to batch multiple messages
      }

      // Reset the real-time mode timeout
      if (realTimeModeTimeoutRef.current) {
        clearTimeout(realTimeModeTimeoutRef.current);
      }

      realTimeModeTimeoutRef.current = setTimeout(() => {
        console.log('[REAL-TIME] Disabling real-time mode due to inactivity');
        setIsRealTimeMode(false);
      }, 5 * 60 * 1000); // 5 minutes of inactivity
    }
  }, [executionId, isRealTimeMode]);


  // Add a function to fetch full artifact content when needed
  const fetchFullArtifactContent = useCallback(async (artifactId: string) => {
    if (!executionId) return null;

    try {
      setLoading(true);
      const response = await fetch(`/api/workflows/executions/${executionId}/artifacts/${artifactId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch artifact: ${response.statusText}`);
      }

      const fullArtifact = await response.json();

      // Update the artifact in our state with full content
      setArtifacts(prev => prev.map(a =>
        a.id === artifactId ? { ...a, content: fullArtifact.content, _contentFetched: true } : a
      ));

      return fullArtifact.content;
    } catch (error) {
      console.error('Error fetching full artifact content:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  // Add a simple, reliable refresh function
  const handleRefreshArtifacts = useCallback(() => {
    console.log('[REFRESH] Manual refresh requested');
    setIsManuallyRefreshing(true);

    // Store current artifacts map for reference
    const currentArtifactsMap = new Map(artifactsRef.current);
    console.log(`[REFRESH-DEBUG] Current artifacts map has ${currentArtifactsMap.size} entries`);

    return workflowExecutionService.getWorkflowExecution(executionId)
      .then(data => {
        console.log('[REFRESH] API call completed successfully');
        if (data) {
          // Update execution state
          setExecution(data);

          // Process all artifacts from the execution
          let artifactsUpdated = 0;
          let artifactsTotal = 0;

          // First, count total artifacts for debugging
          for (const phase of data.phases || []) {
            if (phase.artifacts && phase.artifacts.length > 0) {
              artifactsTotal += phase.artifacts.length;
            }
          }
          console.log(`[REFRESH-DEBUG] API response contains ${artifactsTotal} total artifacts`);

          // Process each phase's artifacts
          for (const phase of data.phases || []) {
            if (phase.artifacts && phase.artifacts.length > 0) {
              console.log(`[REFRESH-DEBUG] Processing ${phase.artifacts.length} artifacts from phase ${phase.id}`);

              for (const artifact of phase.artifacts) {
                // Skip artifacts without ID
                if (!artifact.id) {
                  console.warn(`[REFRESH-DEBUG] Skipping artifact without ID in phase ${phase.id}`);
                  continue;
                }

                // Add phase information
                artifact.phase_id = phase.id;
                artifact.phase_label = phase.label;

                // Use our robust artifact management function
                const wasUpdated = ensureArtifactExists(artifact);
                if (wasUpdated) {
                  artifactsUpdated++;
                  console.log(`[REFRESH-DEBUG] Updated artifact ${artifact.id} from phase ${phase.id}`);
                }
              }
            }
          }

          console.log(`[REFRESH] Processed ${artifactsUpdated} artifact updates from API response`);
          console.log(`[REFRESH-DEBUG] Final artifacts map has ${artifactsRef.current.size} entries`);

          // Extract agent activities
          const allActivities = [];
          for (const phase of data.phases || []) {
            if (phase.agent_activities && phase.agent_activities.length > 0) {
              allActivities.push(...phase.agent_activities.map((a: any) => ({
                ...a,
                phase_id: phase.id,
                phase_label: phase.label
              })));
            }
          }
          setAgentActivities(allActivities);
        }
      })
      .catch(err => {
        console.error('[REFRESH] API call failed:', err);
      })
      .finally(() => {
        setIsManuallyRefreshing(false);
      });
  }, [executionId, ensureArtifactExists]);

  // Handle WebSocket messages
  useEffect(() => {
    if (!lastMessage || !isComponentMountedRef.current) return;

    try {
      const message = JSON.parse(lastMessage.data);
      console.log('[WEBSOCKET] Message received:', message.type);

      // Generate a more unique message ID that includes content hash
      const messageContent = JSON.stringify(message.data || {});
      const contentHash = messageContent.split('').reduce((a, b) => (((a << 5) - a) + b.charCodeAt(0))|0, 0);
      const messageId = `${message.type}-${message.timestamp}-${contentHash}`;

      if (processedMessagesRef.current.has(messageId)) {
        console.log(`[WEBSOCKET] Skipping already processed message: ${message.type}`);
        return;
      }

      // Mark this message as processed
      processedMessagesRef.current.add(messageId);
      console.log(`[WEBSOCKET] Processing message: ${message.type} (ID: ${messageId})`);

      // Limit the size of the processed messages set
      if (processedMessagesRef.current.size > 100) {
        // Keep only the most recent 50 messages
        processedMessagesRef.current = new Set(
          Array.from(processedMessagesRef.current).slice(-50)
        );
      }

      // Keep real-time mode active when receiving WebSocket messages
      if (!isRealTimeMode) {
        console.log('[WEBSOCKET] Enabling real-time mode due to received message');
        setIsRealTimeMode(true);
      }

      // ALWAYS clear any polling interval when we receive a WebSocket message
      // This is critical to prevent unnecessary polling
      if (pollingIntervalRef.current) {
        console.log('[WEBSOCKET] Received message, clearing any existing polling interval:', pollingIntervalRef.current);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Reset the real-time mode timeout
      if (realTimeModeTimeoutRef.current) {
        clearTimeout(realTimeModeTimeoutRef.current);
      }

      realTimeModeTimeoutRef.current = setTimeout(() => {
        console.log('[REAL-TIME] Disabling real-time mode due to inactivity');
        setIsRealTimeMode(false);
      }, 5 * 60 * 1000); // 5 minutes of inactivity

      // Handle artifact creation messages with special care
      if (message.type === 'workflow_artifact_created' && message.data?.artifact) {
        console.log('[ARTIFACT-FLOW-DEBUG] Artifact created message received');

        const artifact = message.data.artifact;
        const artifactId = artifact.id;

        if (!artifactId) {
          console.error('[ARTIFACT-FLOW-DEBUG] Received artifact without ID:', artifact);
          return;
        }

        // Generate a unique message ID for this specific artifact update
        const artifactMessageId = `artifact-${artifactId}-${message.timestamp}`;

        if (processedMessagesRef.current.has(artifactMessageId)) {
          console.log(`[ARTIFACT-FLOW-DEBUG] Skipping already processed artifact message: ${artifactMessageId}`);
          return;
        }

        // Mark this specific artifact message as processed
        processedMessagesRef.current.add(artifactMessageId);

        // Log the current map state before update
        console.log(`[ARTIFACT-FLOW-DEBUG] Before update: Map has ${artifactsRef.current.size} artifacts`);

        // Use our robust artifact management function
        const artifactAdded = ensureArtifactExists(artifact);

        if (artifactAdded) {
          console.log('[ARTIFACT-FLOW-DEBUG] Successfully added/updated artifact in state');
          console.log(`[ARTIFACT-FLOW-DEBUG] After update: Map has ${artifactsRef.current.size} artifacts`);
        } else {
          console.log('[ARTIFACT-FLOW-DEBUG] No changes needed for artifact');
        }

        // Force a refresh after a short delay to ensure we have the latest data
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
          if (fetchExecutionRef.current) {
            console.log('[ARTIFACT-FLOW-DEBUG] Forcing refresh after artifact creation');
            fetchExecutionRef.current();
            refreshTimeoutRef.current = null;
          }
        }, 500);
      }

      // Update cache based on message
      handleWebSocketMessage(message);

      // Force a refresh of the execution data - use a debounced approach to prevent multiple refreshes
      if (fetchExecutionRef.current) {
        // Check if we're within the cooldown period
        const now = Date.now();
        if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN) {
          console.log(`[WEBSOCKET] Skipping immediate refresh - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);

          // Clear any existing timeout
          if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
          }

          // Set a new timeout to refresh after the cooldown period
          refreshTimeoutRef.current = setTimeout(() => {
            console.log('[WEBSOCKET] Debounced refresh after message:', message.type);
            lastRefreshTimeRef.current = Date.now();
            fetchExecutionRef.current();
            refreshTimeoutRef.current = null;
          }, REFRESH_COOLDOWN - (now - lastRefreshTimeRef.current));
        } else {
          // If outside cooldown period, refresh after a small delay to batch multiple messages
          if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
          }

          refreshTimeoutRef.current = setTimeout(() => {
            console.log('[WEBSOCKET] Forcing refresh after message:', message.type);
            lastRefreshTimeRef.current = Date.now();
            fetchExecutionRef.current();
            refreshTimeoutRef.current = null;
          }, 100);
        }
      }

      switch (message.type) {
        case 'workflow_artifact_created':
          console.log('[ARTIFACT-FLOW-DEBUG] Artifact created message received in real-time');
          console.log('[ARTIFACT-FLOW-DEBUG] Artifact ID:', message.data?.artifact?.id);
          console.log('[ARTIFACT-FLOW-DEBUG] Artifact content type:', typeof message.data?.artifact?.content);
          console.log('[ARTIFACT-FLOW-DEBUG] Artifact content length:',
            typeof message.data?.artifact?.content === 'string'
              ? message.data.artifact.content.length
              : 'unknown');

          // Use our centralized artifact management
          if (message.data?.artifact?.id) {
            // Generate a unique message ID for this specific artifact update
            const artifactMessageId = `artifact-${message.data.artifact.id}-${message.timestamp}`;

            if (!processedMessagesRef.current.has(artifactMessageId)) {
              // Mark this specific artifact message as processed
              processedMessagesRef.current.add(artifactMessageId);

              // Log the current map state before update
              console.log(`[ARTIFACT-FLOW-DEBUG] Before update: Map has ${artifactsRef.current.size} artifacts`);

              const newArtifact = message.data.artifact;
              const artifactId = newArtifact.id;
              const phaseId = message.data.phase_id;

              console.log(`[ARTIFACT-FLOW-DEBUG] Processing real-time artifact: ${artifactId} for phase: ${phaseId}`);

              // Find the phase to get its label
              const phase = execution?.phases.find(p => p.id === phaseId);
              const phaseLabel = phase?.label || 'Unknown';

              // Process the artifact once, correctly - create a proper copy to ensure all properties are preserved
              const processedArtifact = {
                ...newArtifact,
                // Ensure phase_label is set
                phase_label: phaseLabel,
                // Ensure tags array exists
                tags: newArtifact.tags || [],
                // Mark as new for animation
                _isNew: true
              };

              // Add phase as a tag if not already present
              if (phaseLabel && !processedArtifact.tags.includes(phaseLabel)) {
                processedArtifact.tags.push(phaseLabel);
              }

              // Add persona as a tag if not already present
              if (processedArtifact.persona_id && !processedArtifact.tags.includes(processedArtifact.persona_id)) {
                processedArtifact.tags.push(processedArtifact.persona_id);
              }

              // Process synthesis artifacts - check both flags and phase label
              if (processedArtifact.is_final_synthesis ||
                  processedArtifact.type === 'synthesis' ||
                  (phaseLabel && phaseLabel.toLowerCase().includes('synthesis'))) {

                // Add 'Synthesis' tag if not already present
                if (!processedArtifact.tags.includes('Synthesis')) {
                  processedArtifact.tags.push('Synthesis');
                }

                // Ensure the is_final_synthesis flag is set
                processedArtifact.is_final_synthesis = true;

                // Ensure the type is set to synthesis
                processedArtifact.type = 'synthesis';

                // Remove redundant tags
                if (processedArtifact.tags.includes('Final Synthesis')) {
                  processedArtifact.tags = processedArtifact.tags.filter(tag => tag !== 'Final Synthesis');
                }

                // Remove lowercase 'synthesis' tag if present
                if (processedArtifact.tags.includes('synthesis')) {
                  processedArtifact.tags = processedArtifact.tags.filter(tag => tag !== 'synthesis');
                }

                console.log(`[SYNTHESIS-DEBUG] Processed synthesis artifact: ${artifactId}, is_final_synthesis=${processedArtifact.is_final_synthesis}, type=${processedArtifact.type}`);
              }

              // Process review artifacts - check both flags and phase label
              if (processedArtifact.is_review ||
                  processedArtifact.type === 'review' ||
                  (phaseLabel && phaseLabel.toLowerCase().includes('review'))) {

                if (!processedArtifact.tags.includes('Review')) {
                  processedArtifact.tags.push('Review');
                }

                // Ensure the is_review flag is set
                processedArtifact.is_review = true;

                // Ensure the type is set to review
                processedArtifact.type = 'review';
              }

              // Log the raw message data
              console.log('[SYNTHESIS-DEBUG] Raw WebSocket message data:', message.data);
              console.log('[SYNTHESIS-DEBUG] Raw artifact from WebSocket:', {
                id: newArtifact.id,
                name: newArtifact.name,
                is_final_synthesis: newArtifact.is_final_synthesis,
                type: newArtifact.type,
                phase_label: phaseLabel
              });

              // Ensure the is_final_synthesis flag is explicitly preserved
              // If it's a synthesis artifact by name but the flag is false, fix it
              if (newArtifact.name &&
                  newArtifact.name.includes('Final Synthesis') &&
                  !newArtifact.is_final_synthesis) {

                console.log('[SYNTHESIS-DEBUG] Fixing synthesis artifact with incorrect flag');
                newArtifact.is_final_synthesis = true;
                newArtifact.type = 'synthesis';

                if (!newArtifact.tags) {
                  newArtifact.tags = [];
                }
                if (!newArtifact.tags.includes('Synthesis')) {
                  newArtifact.tags.push('Synthesis');
                }
              }

              // Immediately update the artifacts state with the new artifact
              setArtifacts(prev => {
                // Use a Map for efficient lookup and deduplication
                const artifactsMap = new Map(prev.map(a => [a.id, a]));

                // CRITICAL FIX: Preserve the is_final_synthesis flag exactly as it comes from the backend
                // Create a proper copy that explicitly preserves all properties
                const processedArtifact = {
                  ...newArtifact,  // Start with all properties from the original artifact
                  id: newArtifact.id,
                  name: newArtifact.name || `Unnamed Artifact`,
                  type: newArtifact.type || 'text',
                  content: newArtifact.content,
                  created_at: newArtifact.created_at || new Date().toISOString(),
                  created_by: newArtifact.created_by,
                  persona_id: newArtifact.persona_id,
                  is_final_synthesis: Boolean(newArtifact.is_final_synthesis),  // Ensure boolean
                  is_review: Boolean(newArtifact.is_review),  // Ensure boolean
                  tags: [...(newArtifact.tags || [])],  // Create a new array to avoid reference issues
                  phase_id: phaseId,
                  phase_label: phaseLabel,
                  _isNew: true,
                  _contentFetched: Boolean(newArtifact.content)
                };

                // Check if this is a synthesis artifact based on phase label
                if (phaseLabel && phaseLabel.toLowerCase() === 'synthesis' &&
                    !processedArtifact.is_final_synthesis &&
                    newArtifact.name && newArtifact.name.toLowerCase().includes('final synthesis')) {
                  processedArtifact.is_final_synthesis = true;
                  processedArtifact.type = 'synthesis';
                  if (!processedArtifact.tags.includes('Synthesis')) {
                    processedArtifact.tags.push('Synthesis');
                  }
                  console.log('[SYNTHESIS-DEBUG] Detected synthesis artifact from name:', newArtifact.name);
                }

                // Log the processed artifact to debug
                console.log('[SYNTHESIS-DEBUG] Processed artifact with explicit properties:', {
                  id: processedArtifact.id,
                  is_final_synthesis: processedArtifact.is_final_synthesis,
                  type: processedArtifact.type,
                  name: processedArtifact.name,
                  tags: processedArtifact.tags,
                  nameContainsFinalSynthesis: processedArtifact.name && processedArtifact.name.includes('Final Synthesis')
                });

                // Double-check if this is a synthesis artifact by name but the flag is still false
                if (processedArtifact.name &&
                    processedArtifact.name.includes('Final Synthesis') &&
                    !processedArtifact.is_final_synthesis) {

                  console.log('[SYNTHESIS-DEBUG] Fixing processed artifact with incorrect synthesis flag');
                  processedArtifact.is_final_synthesis = true;
                  processedArtifact.type = 'synthesis';

                  if (!processedArtifact.tags) {
                    processedArtifact.tags = [];
                  }
                  if (!processedArtifact.tags.includes('Synthesis')) {
                    processedArtifact.tags.push('Synthesis');
                  }
                }

                // Add or update the artifact with the properly preserved flags
                artifactsMap.set(artifactId, processedArtifact);

                // Convert back to array and sort
                const updatedArtifacts = Array.from(artifactsMap.values());
                updatedArtifacts.sort((a, b) => {
                  const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                  const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                  return timeB - timeA;
                });

                console.log(`[ARTIFACT-FLOW-DEBUG] Artifacts state now has ${updatedArtifacts.length} artifacts after real-time update`);

                // Update our ref map to stay in sync with state
                artifactsRef.current = new Map(updatedArtifacts.map(a => [a.id, a]));

                return updatedArtifacts;
              });

              // Immediately dispatch an event to notify components about the new artifact
              setTimeout(() => {
                console.log('[ARTIFACT-FLOW-DEBUG] Dispatching real-time artifact-updated event');
                const artifactEvent = new CustomEvent('artifact-updated', {
                  detail: { artifact: artifactsRef.current.get(artifactId), isNew: true }
                });
                window.dispatchEvent(artifactEvent);

                // Remove the _isNew flag after animation completes
                setTimeout(() => {
                  setArtifacts(prev =>
                    prev.map(a => a.id === artifactId ? {...a, _isNew: false} : a)
                  );
                }, 3000);
              }, 10); // Use a very short timeout to ensure state is updated first

              // Also update the execution state to include this artifact
              setExecution(prev => {
                if (!prev) return prev;

                return {
                  ...prev,
                  phases: prev.phases.map((phase: any) =>
                    phase.id === phaseId
                      ? {
                          ...phase,
                          artifacts: [...(phase.artifacts || []), newArtifact]
                        }
                      : phase
                  )
                };
              });

              console.log(`[ARTIFACT-FLOW-DEBUG] Successfully processed real-time artifact ${artifactId}`);
            } else {
              console.log(`[ARTIFACT-FLOW-DEBUG] Skipping duplicate artifact message: ${artifactMessageId}`);
            }
          }
          break;

        case 'ui_refresh_needed':
          console.log('[WEBSOCKET] UI refresh requested:', message.data?.reason);
          // Force a refresh of the execution data
          if (fetchExecutionRef.current) {
            fetchExecutionRef.current();
          }
          break;

        case WebSocketMessageType.WORKFLOW_EXECUTION_UPDATED:
          if (message.data?.execution) {
            console.log('[WEBSOCKET] Received execution update with timestamp:', message.data.execution.updated_at);
            console.log('[WEBSOCKET-DEBUG] current_phase_id in update:', message.data.execution.current_phase_id);
            console.log('[WEBSOCKET-DEBUG] Full execution update message:', JSON.stringify({
              id: message.data.execution.id,
              current_phase_id: message.data.execution.current_phase_id,
              status: message.data.execution.status,
              phases: message.data.execution.phases?.map(p => ({
                id: p.id,
                status: p.status
              }))
            }));

            if (execution) {
              console.log('[WEBSOCKET-DEBUG] Current execution state before update:', {
                current_phase_id: execution.current_phase_id,
                status: execution.status,
                phases: execution.phases?.map(p => ({
                  id: p.id,
                  status: p.status
                }))
              });
            }

            // Add a check to prevent unnecessary updates
            if (!execution ||
                execution.updated_at !== message.data.execution.updated_at) {

              // Log the update reason
              if (execution) {
                console.log('[WEBSOCKET] Updating execution state because timestamps differ:',
                  'Current:', execution.updated_at,
                  'New:', message.data.execution.updated_at);
              } else {
                console.log('[WEBSOCKET] Setting initial execution state');
              }

              // Preserve the current_phase_id if it's valid and the new one is null
              const newExecution = {...message.data.execution};
              console.log('[WEBSOCKET-DEBUG] Execution update details:', {
                'Current current_phase_id': execution?.current_phase_id,
                'New current_phase_id': newExecution.current_phase_id,
                'Current phase status': execution?.phases?.find(p => p.id === execution?.current_phase_id)?.status
              });

              if (execution &&
                  execution.current_phase_id &&
                  !newExecution.current_phase_id) {

                // Check if the current phase is still in progress
                const currentPhase = execution.phases.find(p => p.id === execution.current_phase_id);
                if (currentPhase && currentPhase.status === 'in_progress') {
                  console.log('[WEBSOCKET-DEBUG] Preserving current_phase_id because phase is still in progress');
                  newExecution.current_phase_id = execution.current_phase_id;
                } else {
                  console.log('[WEBSOCKET-DEBUG] Not preserving current_phase_id because phase is not in progress:', currentPhase?.status);
                }
              }

              // Update execution state with immutability pattern to prevent unnecessary re-renders
              setExecution(prev => {
                if (!prev || prev.updated_at !== message.data.execution.updated_at) {
                  console.log('[WEBSOCKET-DEBUG] Setting execution state to:', {
                    current_phase_id: newExecution.current_phase_id,
                    status: newExecution.status
                  });
                  return newExecution;
                }
                console.log('[WEBSOCKET-DEBUG] No change, preserving execution state');
                return prev; // No change, don't trigger re-render
              });

              setLoading(false);

              // Reset the real-time mode timeout since we got a valid update
              if (realTimeModeTimeoutRef.current) {
                clearTimeout(realTimeModeTimeoutRef.current);
              }

              realTimeModeTimeoutRef.current = setTimeout(() => {
                console.log('[REAL-TIME] Disabling real-time mode due to inactivity');
                setIsRealTimeMode(false);
              }, 5 * 60 * 1000); // 5 minutes of inactivity

              // Extract artifacts from all phases
              const allArtifacts = [];
              for (const phase of message.data.execution.phases || []) {
                if (phase.artifacts && phase.artifacts.length > 0) {
                  allArtifacts.push(...phase.artifacts.map((a: any) => ({
                    ...a,
                    phase_id: phase.id,
                    phase_label: phase.label
                  })));
                }
              }

              // Check for new artifacts
              const existingArtifactIds = new Set(artifacts.map(a => a.id));
              const newArtifacts = allArtifacts.filter(a => !existingArtifactIds.has(a.id));

              if (newArtifacts.length > 0) {
                console.log('[WEBSOCKET] Found', newArtifacts.length, 'new artifacts in execution update');

                // Mark new artifacts for animation
                newArtifacts.forEach(artifact => {
                  artifact._isNew = true;
                });

                // Trigger artifact animations for new artifacts
                setTimeout(() => {
                  newArtifacts.forEach(artifact => {
                    const artifactEvent = new CustomEvent('artifact-updated', {
                      detail: { artifact, isNew: true }
                    });
                    window.dispatchEvent(artifactEvent);
                  });

                  // Remove the _isNew flag after animation completes
                  setTimeout(() => {
                    setArtifacts(prev =>
                      prev.map(a => newArtifacts.some(na => na.id === a.id) ? {...a, _isNew: false} : a)
                    );
                  }, 3000);
                }, 100);
              }

              // Update artifacts with immutability pattern - PRESERVE EXISTING ARTIFACTS
              setArtifacts(prevArtifacts => {
                // Create a map of existing artifacts by ID for quick lookup
                const artifactMap = new Map();

                // Add existing artifacts to the map
                prevArtifacts.forEach(artifact => {
                  artifactMap.set(artifact.id, artifact);
                });

                // Add or update with new artifacts
                allArtifacts.forEach(artifact => {
                  artifactMap.set(artifact.id, artifact);
                });

                // Convert map back to array
                const mergedArtifacts = Array.from(artifactMap.values());

                // Sort by creation time (newest first)
                mergedArtifacts.sort((a, b) => {
                  const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                  const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                  return timeB - timeA;
                });

                console.log('[WEBSOCKET] Updated artifacts list with', mergedArtifacts.length, 'artifacts (merged)');
                return mergedArtifacts;
              });

              // Extract agent activities from all phases
              const allActivities = [];
              for (const phase of message.data.execution.phases || []) {
                if (phase.agent_activities && phase.agent_activities.length > 0) {
                  allActivities.push(...phase.agent_activities.map((a: any) => ({
                    ...a,
                    phase_id: phase.id,
                    phase_label: phase.label
                  })));
                }
              }

              // Update agent activities with immutability pattern and deduplication
              setAgentActivities(prevActivities => {
                // Create a map of existing activities by ID
                const activityMap = new Map();

                // Add all activities to the map, with newer versions overwriting older ones
                // First add existing activities
                prevActivities.forEach(activity => {
                  if (activity.id) {
                    activityMap.set(activity.id, activity);
                  }
                });

                // Then add new activities, which will overwrite any duplicates
                allActivities.forEach(activity => {
                  if (activity.id) {
                    activityMap.set(activity.id, activity);
                  }
                });

                // Convert map back to array
                const deduplicatedActivities = Array.from(activityMap.values());

                // Sort by timestamp if available
                deduplicatedActivities.sort((a, b) => {
                  const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                  const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                  return timeB - timeA; // Newest first
                });

                // Check if activities actually changed
                const hasChanges = deduplicatedActivities.length !== prevActivities.length ||
                  JSON.stringify(deduplicatedActivities) !== JSON.stringify(prevActivities);

                if (hasChanges) {
                  console.log('[WEBSOCKET] Updated agent activities list with', deduplicatedActivities.length, 'activities (deduplicated)');
                  return deduplicatedActivities;
                }
                console.log('[WEBSOCKET] Agent activities unchanged, preserving reference');
                return prevActivities; // No change, don't trigger re-render
              });
            } else {
              console.log('[WEBSOCKET] Skipping execution update - no changes detected');
            }
          } else {
            console.warn('[WEBSOCKET] Received execution update with no execution data');
          }
          break;

        case WebSocketMessageType.WORKFLOW_PHASE_UPDATED:
          // Update a specific phase
          console.log('[WEBSOCKET-DEBUG] Received phase update:', {
            phase_id: message.data.phase_id,
            status: message.data.status
          });

          if (execution) {
            console.log('[WEBSOCKET-DEBUG] Current execution state before phase update:', {
              current_phase_id: execution.current_phase_id,
              phases: execution.phases?.map(p => ({
                id: p.id,
                status: p.status,
                artifacts_count: p.artifacts?.length || 0
              }))
            });

            setExecution(prev => {
              // Make a deep copy to ensure React detects the change
              const updatedExecution = JSON.parse(JSON.stringify(prev));

              // Find and update the phase
              const phaseIndex = updatedExecution.phases.findIndex(p => p.id === message.data.phase_id);
              if (phaseIndex >= 0) {
                // Preserve artifacts before updating status
                const existingArtifacts = prev.phases[phaseIndex].artifacts || [];

                updatedExecution.phases[phaseIndex].status = message.data.status;

                // Ensure artifacts are preserved
                updatedExecution.phases[phaseIndex].artifacts = existingArtifacts;

                // If this phase is now in progress, make it the current phase
                if (message.data.status === 'in_progress') {
                  updatedExecution.current_phase_id = message.data.phase_id;
                }

                console.log('[WEBSOCKET-DEBUG] Preserved', existingArtifacts.length, 'artifacts for phase', message.data.phase_id);
              }

              console.log('[WEBSOCKET-DEBUG] Updated execution state after phase update:', {
                current_phase_id: updatedExecution.current_phase_id,
                updated_phase: {
                  id: message.data.phase_id,
                  status: message.data.status,
                  artifacts_count: updatedExecution.phases.find(p => p.id === message.data.phase_id)?.artifacts?.length || 0
                }
              });

              return updatedExecution;
            });

            // Force a refresh after a short delay to ensure UI is updated
            setTimeout(() => {
              if (fetchExecutionRef.current) {
                console.log('[WEBSOCKET] Forcing refresh after phase status update');
                fetchExecutionRef.current();
              }
            }, 500);

            console.log('[WEBSOCKET] Updated phase status:', message.data.phase_id, 'to', message.data.status);
          }
          break;

        case WebSocketMessageType.WORKFLOW_PHASE_STARTED:
          // Update the current phase
          if (execution) {
            setExecution(prev => ({
              ...prev,
              current_phase_id: message.data.phase_id,
              phases: prev.phases.map((phase: any) =>
                phase.id === message.data.phase_id
                  ? {
                      ...phase,
                      status: 'in_progress',
                      start_time: message.data.start_time
                    }
                  : phase
              )
            }));
            console.log('[WEBSOCKET] Phase started:', message.data.phase_id);
          }
          break;

        case WebSocketMessageType.WORKFLOW_EXECUTION_COMPLETED:
          // Update execution status
          setExecution(prev => ({
            ...prev,
            status: 'completed',
            end_time: message.data.end_time
          }));
          console.log('[WEBSOCKET] Workflow execution completed');
          break;

        case WebSocketMessageType.WORKFLOW_EXECUTION_PAUSED:
          // Update execution status to paused
          setExecution(prev => ({
            ...prev,
            status: 'paused',
            updated_at: message.data.updated_at
          }));
          console.log('[WEBSOCKET] Workflow execution paused');
          break;

        case WebSocketMessageType.WORKFLOW_EXECUTION_RESUMED:
          // Update execution status to active
          setExecution(prev => ({
            ...prev,
            status: 'active',
            updated_at: message.data.updated_at
          }));
          console.log('[WEBSOCKET] Workflow execution resumed');
          break;

        case WebSocketMessageType.WORKFLOW_PHASE_CONFIG_UPDATED:
          // Update phase config
          if (execution) {
            setExecution(prev => ({
              ...prev,
              phases: prev.phases.map((phase: any) =>
                phase.id === message.data.phase_id
                  ? {
                      ...phase,
                      config: message.data.config
                    }
                  : phase
              )
            }));
            console.log('[WEBSOCKET] Phase config updated:', message.data.phase_id);
          }
          break;

        case WebSocketMessageType.WORKFLOW_ARTIFACT_CREATED:
          console.log('[ARTIFACT-FLOW-DEBUG] Artifact created message received in real-time');

          if (message.data?.artifact && message.data?.artifact?.id) {
            const newArtifact = message.data.artifact;
            const artifactId = newArtifact.id;
            const phaseId = message.data.phase_id;

            console.log('[ARTIFACT-FLOW-DEBUG] Processing real-time artifact:', artifactId, 'for phase:', phaseId);

            // Generate a unique message ID for this specific artifact update
            const artifactMessageId = `artifact-${artifactId}-${message.timestamp}`;

            if (!processedMessagesRef.current.has(artifactMessageId)) {
              // Mark this specific artifact message as processed
              processedMessagesRef.current.add(artifactMessageId);

              // Update execution state to add the new artifact to the appropriate phase
              setExecution(prev => {
                if (!prev) return prev;

                // Create a deep copy
                const updatedExecution = JSON.parse(JSON.stringify(prev));

                // Find the phase
                const phaseIndex = updatedExecution.phases.findIndex(p => p.id === phaseId);
                if (phaseIndex >= 0) {
                  // Initialize artifacts array if it doesn't exist
                  if (!updatedExecution.phases[phaseIndex].artifacts) {
                    updatedExecution.phases[phaseIndex].artifacts = [];
                  }

                  // Use a Map for efficient lookup
                  const artifactsMap = new Map(
                    updatedExecution.phases[phaseIndex].artifacts.map(a => [a.id, a])
                  );

                  // Add or update the artifact
                  artifactsMap.set(artifactId, newArtifact);

                  // Replace the artifacts array with the updated values
                  updatedExecution.phases[phaseIndex].artifacts = Array.from(artifactsMap.values());

                  console.log('[ARTIFACT-FLOW-DEBUG] Phase now has', updatedExecution.phases[phaseIndex].artifacts.length, 'artifacts in real-time');
                }

                return updatedExecution;
              });

              // Update the artifacts state more efficiently
              setArtifacts(prev => {
                // Use a Map for efficient lookup and deduplication
                const artifactsMap = new Map(prev.map(a => [a.id, a]));

                // Add or update the new artifact
                artifactsMap.set(artifactId, {
                  ...newArtifact,
                  phase_id: phaseId,
                  phase_label: execution?.phases.find(p => p.id === phaseId)?.label || 'Unknown',
                  _isNew: true,
                  _contentFetched: Boolean(newArtifact.content)
                });

                // Convert back to array and sort
                const updatedArtifacts = Array.from(artifactsMap.values());
                updatedArtifacts.sort((a, b) => {
                  const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                  const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                  return timeB - timeA;
                });

                console.log('[ARTIFACT-FLOW-DEBUG] Artifacts state now has', updatedArtifacts.length, 'artifacts after real-time update');

                // Update our ref map to stay in sync with state
                artifactsRef.current = artifactsMap;

                return updatedArtifacts;
              });

              // If this is a synthesis or review artifact, fetch full content immediately
              const artifact = artifactsRef.current.get(artifactId);
              if (artifact && (artifact.is_final_synthesis || artifact.is_review) && artifact.has_full_content) {
                fetchFullArtifactContent(artifactId);
              }

              // Immediately dispatch an event to notify components about the new artifact
              setTimeout(() => {
                console.log('[ARTIFACT-FLOW-DEBUG] Dispatching real-time artifact-updated event');
                const artifactEvent = new CustomEvent('artifact-updated', {
                  detail: { artifact: artifactsRef.current.get(artifactId), isNew: true }
                });
                window.dispatchEvent(artifactEvent);

                // Remove the _isNew flag after animation completes
                setTimeout(() => {
                  console.log('[ARTIFACT-FLOW-DEBUG] Removing _isNew flag');
                  setArtifacts(prev =>
                    prev.map(a => a.id === artifactId ? {...a, _isNew: false} : a)
                  );
                }, 3000);
              }, 10); // Use a very short timeout to ensure state is updated first
            } else {
              console.log(`[ARTIFACT-FLOW-DEBUG] Skipping duplicate artifact message: ${artifactMessageId}`);
            }
          }
          break;

        case 'workflow_agent_active':
        case 'workflow_agent_idle':
        case 'workflow_agent_tool_usage':
          // Add new agent activity
          if (message.data?.activity) {
            const newActivity = {
              ...message.data.activity,
              phase_id: message.data.phase_id,
              phase_label: execution?.phases.find((p: any) => p.id === message.data.phase_id)?.label || 'Unknown'
            };

            // Check if this activity already exists
            const activityExists = agentActivities.some(a => a.id === newActivity.id);

            if (!activityExists) {
              // Update the phase's agent activities
              setExecution(prev => ({
                ...prev,
                phases: prev.phases.map((phase: any) =>
                  phase.id === message.data.phase_id
                    ? {
                        ...phase,
                        agent_activities: [...(phase.agent_activities || []), message.data.activity]
                      }
                    : phase
                )
              }));

              // Update the agent activities list
              setAgentActivities(prev => [...prev, newActivity]);
              console.log('[WEBSOCKET] Added new agent activity:', message.type, 'for agent', newActivity.agent_id);

              // Check if this activity has a related artifact
              if (newActivity.artifact_id) {
                console.log('[ARTIFACT-DEBUG] Agent activity references artifact:', newActivity.artifact_id);

                // Find the artifact in our current state
                const relatedArtifact = artifacts.find(a => a.id === newActivity.artifact_id);

                if (relatedArtifact) {
                  console.log('[ARTIFACT-DEBUG] Found referenced artifact in state');
                } else {
                  console.log('[ARTIFACT-DEBUG] Referenced artifact not found in state, forcing refresh');
                  // Force a refresh to get the missing artifact
                  if (fetchExecutionRef.current) {
                    fetchExecutionRef.current();
                  }
                }
              }

              // Force update of the AgentActivityTimeline
              setTimeout(() => {
                const activityEvent = new CustomEvent('agent-activity-updated', { detail: newActivity });
                window.dispatchEvent(activityEvent);
              }, 100);
            } else {
              console.log('[WEBSOCKET] Skipping duplicate agent activity:', newActivity.id);
            }
          }
          break;

        case 'committee_execution_started':
          console.log('[WEBSOCKET] Committee execution started:', message.data.committee_mode);
          setCommitteeState({
            mode: message.data.committee_mode,
            currentPersonaIndex: -1,
            personas: message.data.personas.map((p: any) => ({
              ...p,
              status: 'waiting'
            })),
            iteration: 1,
            totalIterations: message.data.total_iterations || 1,
            phase: 'initial'
          });
          break;

        case 'committee_persona_active':
          console.log('[WEBSOCKET] Committee persona active:', message.data.persona_index);
          setCommitteeState(prev => ({
            ...prev,
            currentPersonaIndex: message.data.persona_index,
            personas: prev.personas.map((p: any, i: number) =>
              i === message.data.persona_index
                ? { ...p, status: 'active', start_time: message.data.timestamp }
                : p
            )
          }));
          break;

        case 'committee_persona_completed':
          console.log('[WEBSOCKET] Committee persona completed:', message.data.persona_index);
          setCommitteeState(prev => ({
            ...prev,
            personas: prev.personas.map((p: any, i: number) =>
              i === message.data.persona_index
                ? { ...p, status: 'completed', end_time: message.data.timestamp }
                : p
            )
          }));
          break;

        case 'committee_iteration_started':
          console.log('[WEBSOCKET] Committee iteration started:', message.data.iteration);
          setCommitteeState(prev => ({
            ...prev,
            iteration: message.data.iteration,
            currentPersonaIndex: -1,
            personas: prev.personas.map((p: any) => ({
              ...p,
              status: 'waiting',
              start_time: undefined,
              end_time: undefined
            }))
          }));
          break;

        case 'committee_phase_changed':
          console.log('[WEBSOCKET] Committee phase changed:', message.data.phase);
          setCommitteeState(prev => ({
            ...prev,
            phase: message.data.phase
          }));
          break;

        case 'committee_execution_completed':
          console.log('[WEBSOCKET] Committee execution completed');
          setCommitteeState(prev => ({
            ...prev,
            currentPersonaIndex: -1,
            personas: prev.personas.map((p: any) => ({
              ...p,
              status: 'completed'
            }))
          }));
          break;

        case WebSocketMessageType.COMMITTEE_REVIEW_STARTED:
          console.log('[WEBSOCKET] Committee review started');
          setCommitteeState(prev => ({
            ...prev,
            phase: 'review',
            reviewer: message.data?.reviewer_id
          }));
          break;

        case WebSocketMessageType.COMMITTEE_REVIEW_COMPLETED:
          console.log('[WEBSOCKET] Committee review completed');
          setCommitteeState(prev => ({
            ...prev,
            phase: 'completed'
          }));
          break;

        case 'error':
          console.error('WebSocket error:', message.data?.message);
          break;

        case WebSocketMessageType.PING:
        case WebSocketMessageType.PONG:
          console.log('[WEBSOCKET] Received ping/pong message');
          break;

        default:
          console.log('[WEBSOCKET] Connection established with server');
          // Immediately request the latest execution state
          if (connectionStatus === 'Open') {
            console.log('[WEBSOCKET] Requesting latest execution state after connection established');
            sendCommand('request_execution_state');

            // Enable real-time mode when connection is established
            setIsRealTimeMode(true);

            // Reset the real-time mode timeout
            if (realTimeModeTimeoutRef.current) {
              clearTimeout(realTimeModeTimeoutRef.current);
            }

            realTimeModeTimeoutRef.current = setTimeout(() => {
              console.log('[REAL-TIME] Disabling real-time mode due to inactivity');
              setIsRealTimeMode(false);
            }, 5 * 60 * 1000); // 5 minutes of inactivity
          }
          break;
      }
    } catch (err) {
      console.error('Error handling WebSocket message:', err);
    }
  }, [lastMessage, execution, isRealTimeMode]);


  // Define the fetch function
  useEffect(() => {
    // Define the fetch function
    const fetchExecution = async () => {
      if (!executionId) return;

      // Check if we're within the cooldown period
      const now = Date.now();
      if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN) {
        console.log(`[POLLING] Skipping refresh - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
        return;
      }

      try {
        setLoading(true);
        lastRefreshTimeRef.current = now;
        console.log('[POLLING] Fetching execution data');

        // Validate executionId before making the API call
        if (typeof executionId !== 'string' || !executionId.trim()) {
          console.error(`[POLLING] Invalid executionId: ${executionId}`);
          setError(`Invalid execution ID: ${executionId}`);
          setLoading(false);
          return;
        }

        const data = await workflowExecutionService.getWorkflowExecution(executionId);

        if (!data) {
          console.error(`[POLLING] No data returned for execution ${executionId}`);
          setError(`Failed to fetch execution data for ID: ${executionId}`);
          setLoading(false);
          return;
        }

        setExecution(data);

        // Process all artifacts from the execution
        let artifactsProcessed = 0;

        // Process each phase's artifacts
        for (const phase of data.phases || []) {
          if (phase.artifacts && phase.artifacts.length > 0) {
            for (const artifact of phase.artifacts) {
              // Skip artifacts without ID
              if (!artifact.id) continue;

              // Add phase information
              artifact.phase_id = phase.id;
              artifact.phase_label = phase.label;

              // Use our robust artifact management function
              const wasUpdated = ensureArtifactExists(artifact);
              if (wasUpdated) artifactsProcessed++;
            }
          }
        }

        console.log(`[POLLING] Processed ${artifactsProcessed} artifacts from initial fetch`);

        // Extract agent activities from all phases
        const allActivities = [];
        for (const phase of data.phases || []) {
          if (phase.agent_activities && phase.agent_activities.length > 0) {
            allActivities.push(...phase.agent_activities.map((a: any) => ({
              ...a,
              phase_id: phase.id,
              phase_label: phase.label
            })));
          }
        }
        setAgentActivities(allActivities);
        console.log('[POLLING] Updated agent activities list with', allActivities.length, 'activities');

        // Fetch personas for all assignments
        const personaIds = new Set<string>();
        data.phases.forEach((phase: any) => {
          (phase.config?.personas || []).forEach((assignment: any) => {
            personaIds.add(assignment.persona_id);
          });
        });

        const personaData: Record<string, any> = {};
        for (const id of personaIds) {
          try {
            const persona = await personaService.getPersona(id);
            personaData[id] = persona;
          } catch (err) {
            console.error(`Failed to fetch persona ${id}`, err);
          }
        }

        setPersonas(personaData);

        // Update last polling time
        setLastPollingTime(new Date());
        console.log('[POLLING] Fetch completed at', new Date().toLocaleTimeString());
      } catch (err) {
        setError("Failed to fetch execution data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Store the fetch function in the ref
    fetchExecutionRef.current = fetchExecution;

    // Initial fetch
    fetchExecution();
  }, [executionId, ensureArtifactExists]);


  // Check if all phases are complete and update execution status
  useEffect(() => {
    if (execution && execution.phases && execution.phases.length > 0) {
      const allPhasesComplete = execution.phases.every(
        phase => phase.status === 'completed' || phase.status === 'failed' || phase.status === 'skipped'
      );

      if (allPhasesComplete && execution.status === 'active') {
        console.log('All phases complete, updating execution status to completed');
        // Update local state immediately
        setExecution(prev => ({
          ...prev,
          status: 'completed',
          end_time: prev.end_time || new Date().toISOString()
        }));

        // Optionally send update to server if needed
        if (connectionStatus === 'Open') {
          sendCommand('complete_execution');
        }
      }
    }
  }, [execution?.phases, execution?.status, connectionStatus, sendCommand]);

  // WebSocket-only approach - no polling
  useEffect(() => {
    // Clear any existing polling interval (legacy cleanup)
    if (pollingIntervalRef.current) {
      console.log('[WEBSOCKET-ONLY] Clearing any existing polling interval', pollingIntervalRef.current);
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Update the previous connection status ref
    prevConnectionStatusRef.current = connectionStatus;

    // Log the current approach
    console.log('[WEBSOCKET-ONLY] Using WebSocket-only approach for real-time updates');

  }, [connectionStatus]);

  // When a phase is running, force real-time mode
  useEffect(() => {
    if (execution && execution.status === 'active' && execution.current_phase_id) {
      const currentPhase = execution.phases.find(p => p.id === execution.current_phase_id);
      if (currentPhase && currentPhase.status === 'in_progress') {
        console.log('[REAL-TIME] Forcing real-time mode because a phase is in progress');
        setIsRealTimeMode(true);

        // Clear any polling interval
        if (pollingIntervalRef.current) {
          console.log('[REAL-TIME] Clearing polling interval because a phase is in progress');
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }
  }, [execution]);



  // Add this function to handle reviewing phase inputs before execution
  const handleReviewPhaseInputs = async (phaseId) => {
    if (!executionId) return;

    console.log(`[PHASE-INPUTS] Starting handleReviewPhaseInputs for phase ${phaseId} in execution ${executionId}`);

    try {
      setLoading(true);
      setSelectedPhaseId(phaseId);
      setErrorMessage(null); // Clear any previous errors

      console.log(`[PHASE-INPUTS] About to call phaseExecutionService.getPhaseInputs(${executionId}, ${phaseId})`);

      // Fetch phase inputs
      const inputs = await phaseExecutionService.getPhaseInputs(executionId, phaseId);

      console.log(`[PHASE-INPUTS] Received response from getPhaseInputs:`, inputs);

      // Check if there's an error in the response
      if (inputs.error) {
        console.error(`[PHASE-INPUTS] Error in phase inputs response:`, inputs.error);
        setErrorMessage(`Failed to get phase inputs: ${inputs.error}`);

        // Auto-clear the error after 5 seconds
        if (errorTimeout) clearTimeout(errorTimeout);
        const timeout = setTimeout(() => setErrorMessage(null), 5000);
        setErrorTimeout(timeout);

        return; // Don't show the modal if there's an error
      }

      console.log(`[PHASE-INPUTS] Setting phase inputs and showing modal`);
      setPhaseInputs(inputs);

      // Show the review modal
      setShowInputReviewModal(true);
    } catch (err) {
      console.error(`[PHASE-INPUTS] Exception in handleReviewPhaseInputs:`, err);
      console.error(`[PHASE-INPUTS] Error details:`, err.response?.data || err.message);

      // Set error message
      setErrorMessage(`Failed to get phase inputs: ${err.response?.data?.error || err.message || 'Unknown error'}`);

      // Auto-clear the error after 5 seconds
      if (errorTimeout) clearTimeout(errorTimeout);
      const timeout = setTimeout(() => setErrorMessage(null), 5000);
      setErrorTimeout(timeout);
    } finally {
      console.log(`[PHASE-INPUTS] handleReviewPhaseInputs completed, loading state set to false`);
      setLoading(false);
    }
  };

  // Add this function to run the phase after review
  const runPhaseAfterReview = () => {
    if (selectedPhaseId) {
      // First close the modal immediately to return to the workflow view
      setShowInputReviewModal(false);

      // Then trigger the phase execution in the background
      // This will happen asynchronously without blocking the UI
      handleRunPhase(selectedPhaseId);
    }
  };

  // Modify the handleRunPhase function to be more non-blocking and prioritize WebSocket updates
  const handleRunPhase = async (phaseId) => {
    if (!executionId) return;

    try {
      // Update UI immediately to show committee is starting
      const phase = execution?.phases?.find(p => p.id === phaseId);

      console.log(`PHASE DEBUG: Attempting to run phase ${phaseId} with current status: ${phase?.status}`);

      // Check if phase is already completed
      if (phase?.status === 'completed') {
        setErrorMessage("This phase has already been completed and cannot be executed again.");

        // Auto-clear the error after 5 seconds
        if (errorTimeout) clearTimeout(errorTimeout);
        const timeout = setTimeout(() => setErrorMessage(null), 5000);
        setErrorTimeout(timeout);
        return;
      }

      if (phase?.config?.personas) {
        setCommitteeState({
          mode: committeeMode,
          currentPersonaIndex: -1,
          personas: phase.config.personas.map(p => ({
            persona_id: p.persona_id,
            role: p.role,
            status: 'waiting'
          })),
          iteration: 1,
          totalIterations: committeeMode === 'iterative' ? 3 : 1,
          phase: 'initial'
        });

        console.log(`Setting up committee visualization with ${phase.config.personas.length} personas`);
      }

      // Update phase status in UI immediately for better responsiveness
      setExecution(prev => {
        // Log the current state for debugging
        console.log('[PHASE-EXECUTION] Current execution state before update:', {
          current_phase_id: prev.current_phase_id,
          phase_status: prev.phases.find(p => p.id === phaseId)?.status,
          artifacts_count: prev.phases.find(p => p.id === phaseId)?.artifacts?.length || 0
        });

        // Create a deep copy to ensure React detects the change
        const updatedExecution = JSON.parse(JSON.stringify(prev));

        // Find and update the phase
        const phaseIndex = updatedExecution.phases.findIndex(p => p.id === phaseId);
        if (phaseIndex >= 0) {
          // Preserve artifacts before updating status
          const existingArtifacts = prev.phases[phaseIndex].artifacts || [];

          updatedExecution.phases[phaseIndex].status = 'in_progress';
          updatedExecution.phases[phaseIndex].start_time = new Date().toISOString();

          // Ensure artifacts are preserved
          updatedExecution.phases[phaseIndex].artifacts = existingArtifacts;

          console.log('[PHASE-EXECUTION] Preserved', existingArtifacts.length, 'artifacts for phase', phaseId);
        }

        // Set the current phase ID
        updatedExecution.current_phase_id = phaseId;

        console.log('[PHASE-EXECUTION] Updated execution state:', {
          current_phase_id: updatedExecution.current_phase_id,
          phase_status: updatedExecution.phases.find(p => p.id === phaseId)?.status,
          artifacts_count: updatedExecution.phases.find(p => p.id === phaseId)?.artifacts?.length || 0
        });

        return updatedExecution;
      });

      // Set loading state for this specific phase, not the whole UI
      // This allows the UI to remain interactive
      setLoading(false);

      // Force real-time mode when running a phase
      setIsRealTimeMode(true);

      // Clear any polling interval
      if (pollingIntervalRef.current) {
        console.log('[PHASE-EXECUTION] Clearing polling interval to prioritize WebSocket updates');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Force a refresh of the WebSocket connection
      if (connectionStatus !== 'Open') {
        console.log('[WEBSOCKET] Connection not open, attempting to reconnect');
        reconnect();
      } else {
        console.log('[WEBSOCKET] Connection is open, requesting latest state');
        sendCommand('request_execution_state');
      }

      // Set a timeout to force refresh if no updates are received
      const forceRefreshTimeout = setTimeout(() => {
        console.log('[FORCE-REFRESH] No updates received, forcing refresh');
        if (fetchExecutionRef.current) {
          fetchExecutionRef.current();
        }
      }, 5000); // 5 seconds

      // Execute the phase with the selected committee mode
      try {
        const response = await phaseExecutionService.executePhase(executionId, phaseId, committeeMode);
        console.log(`Phase execution started with ${committeeMode} committee mode, context ID: ${response.context_id}`);

        // Request latest state again after execution starts
        if (connectionStatus === 'Open') {
          setTimeout(() => {
            sendCommand('request_execution_state');
          }, 500); // Small delay to allow backend to process
        }

        // Clear the force refresh timeout
        clearTimeout(forceRefreshTimeout);

        // Set a timeout to disable real-time mode after a period of inactivity
        if (realTimeModeTimeoutRef.current) {
          clearTimeout(realTimeModeTimeoutRef.current);
        }

        realTimeModeTimeoutRef.current = setTimeout(() => {
          console.log('[REAL-TIME] Disabling real-time mode due to inactivity');
          setIsRealTimeMode(false);
        }, 5 * 60 * 1000); // 5 minutes of inactivity

        // Force a refresh after a short delay to ensure we have the latest data
        setTimeout(() => {
          if (fetchExecutionRef.current) {
            console.log('[PHASE-EXECUTION] Forcing refresh after phase start');
            fetchExecutionRef.current();
          }
        }, 2000);
      } catch (err) {
        console.error("Failed to run phase", err);

        // Use the enhanced error message if available
        const errorMessage = err.userMessage || err.response?.data?.detail || err.message || 'Unknown error';

        // Display error message in UI
        setErrorMessage(`Failed to start phase execution: ${errorMessage}`);

        // Auto-clear the error after 5 seconds
        if (errorTimeout) clearTimeout(errorTimeout);
        const timeout = setTimeout(() => setErrorMessage(null), 5000);
        setErrorTimeout(timeout);

        // If the phase was already completed, update the UI to reflect that
        if (errorMessage.includes('already been completed') ||
            (err.response?.data?.detail && err.response.data.detail.includes('completed state'))) {
          setExecution(prev => ({
            ...prev,
            phases: prev.phases.map(p =>
              p.id === phaseId
                ? { ...p, status: 'completed' }
                : p
            )
          }));
        }

        // Don't disable real-time mode on error
        clearTimeout(forceRefreshTimeout);
      }
    } catch (err) {
      console.error("Error setting up phase execution", err);
      // Don't block the UI even if there's an error
      setLoading(false);

      // Display error message in UI
      setErrorMessage(`Error setting up phase execution: ${err.message || 'Unknown error'}`);

      // Auto-clear the error after 5 seconds
      if (errorTimeout) clearTimeout(errorTimeout);
      const timeout = setTimeout(() => setErrorMessage(null), 5000);
      setErrorTimeout(timeout);
    }
  };

  // Handle phase completion
  const handleCompletePhase = async (phaseId: string) => {
    if (!executionId) return;

    try {
      if (connectionStatus === 'Open') {
        // Use WebSocket for real-time update
        console.log(`Sending phase completion command via WebSocket for phase ${phaseId}`);
        sendCommand('update_phase', {
          phase_id: phaseId,
          status: 'completed'
        });
      } else {
        // Fall back to REST API
        console.log(`Using REST API for phase completion for phase ${phaseId}`);
        const updatedExecution = await workflowExecutionService.updatePhaseStatus(
          executionId,
          phaseId,
          'completed'
        );
        setExecution(updatedExecution);
      }
    } catch (err) {
      console.error("Failed to complete phase", err);

      // Display error message in UI
      setErrorMessage(`Failed to complete phase: ${err.response?.data?.detail || err.message || 'Unknown error'}`);

      // Auto-clear the error after 5 seconds
      if (errorTimeout) clearTimeout(errorTimeout);
      const timeout = setTimeout(() => setErrorMessage(null), 5000);
      setErrorTimeout(timeout);
    }
  };

  // Calculate progress
  const calculateProgress = (phases: any[]) => {
    if (!phases || phases.length === 0) return 0;

    const completed = phases.filter(p =>
      p.status === 'completed' || p.status === 'skipped'
    ).length;

    return Math.round((completed / phases.length) * 100);
  };

  // Add effect to log artifacts state changes
  useEffect(() => {
    console.log('[ARTIFACTS-DEBUG] Artifacts state updated:', artifacts.length, 'artifacts');
    artifacts.forEach((artifact, index) => {
      // Log all artifacts for complete debugging
      console.log(`[ARTIFACTS-DEBUG] Artifact ${index}:`, {
        id: artifact.id,
        name: artifact.name,
        phase_id: artifact.phase_id,
        created_at: artifact.created_at,
        is_final_synthesis: artifact.is_final_synthesis,
        type: artifact.type,
        tags: artifact.tags
      });
    });

    // Update our ref map to stay in sync with state
    const newMap = new Map();
    artifacts.forEach(artifact => {
      if (artifact.id) {
        newMap.set(artifact.id, artifact);
      }
    });
    artifactsRef.current = newMap;

    // Log the current map size for debugging
    console.log(`[ARTIFACTS-MAP-DEBUG] Map now has ${artifactsRef.current.size} artifacts`);
  }, [artifacts]);

  // Add connection status indicator with PollingIndicator component
  const renderConnectionStatus = () => {
    return (
      <PollingIndicator
        connectionStatus={connectionStatus}
        isPolling={false} // Always false since we're not using polling
        lastPollingTime={lastPollingTime}
        isManuallyRefreshing={isManuallyRefreshing}
        executionStatus={execution?.status} // Pass the execution status
        isRealTimeMode={isRealTimeMode} // Pass the real-time mode state
        onManualRefresh={() => {
          if (connectionStatus === 'Open') {
            console.log('[MANUAL-REFRESH] WebSocket connected, requesting latest state');
            sendCommand('request_execution_state');
            setLastPollingTime(new Date());
          } else {
            console.log('[MANUAL-REFRESH] WebSocket not connected, using API call');
            setIsManuallyRefreshing(true);
            handleRefreshArtifacts()
              .then(() => {
                setLastPollingTime(new Date());
                console.log('[MANUAL-REFRESH] API refresh completed');
              })
              .finally(() => {
                setIsManuallyRefreshing(false);
              });
          }
        }}
      />
    );
  };

  if (loading) return <div className="p-4">Loading execution data...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  if (!execution) return <div className="p-4">No execution data found</div>;

  // Find the current active phase
  const currentPhase = execution.phases.find((p: any) => p.id === execution.current_phase_id);

  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
      <div className="mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Workflow Execution</h2>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Session: {execution.session_id} • Started: {new Date(execution.created_at).toLocaleString()}
            </div>
          </div>
          
          {/* Move top-level tab navigation to the right */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
              <li className="mr-2">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg ${
                    activeTab !== 'workflow_replay'
                      ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                      : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                  }`}
                  onClick={() => setActiveTab('workflow')}
                >
                  Execution
                </button>
              </li>
              <li className="mr-2">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg ${
                    activeTab === 'workflow_replay'
                      ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                      : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                  }`}
                  onClick={() => setActiveTab('workflow_replay')}
                >
                  Replay
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-between">
          {/* Connection status indicator moved to execution tab */}
        </div>

        {/* Error message toast */}
        {errorMessage && (
          <div className="fixed top-4 right-4 z-50 max-w-md">
            <div className={`rounded-lg shadow-lg p-4 ${darkMode ? 'bg-red-900 text-white' : 'bg-red-100 text-red-800'} flex items-start`}>
              <div className="flex-shrink-0 mr-2">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="ml-4 text-sm font-medium focus:outline-none"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'workflow_replay' ? (
        // Workflow Replay Tab (top-level)
        <div className="w-full">
          {execution && (
            <RelatedExecutionsView
              workflowId={execution.workflow_id}
              currentExecutionId={executionId}
              darkMode={darkMode}
            />
          )}
        </div>
      ) : (
        // Execution Tab Content (includes the grid with workflow/debug tabs)
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            {/* Sub-tabs for workflow and debug views */}
            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
              <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
                <li className="mr-2">
                  <button
                    className={`inline-block p-4 border-b-2 rounded-t-lg ${
                      activeTab === 'workflow'
                        ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                        : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('workflow')}
                  >
                    Workflow
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    className={`inline-block p-4 border-b-2 rounded-t-lg ${
                      activeTab === 'debug'
                        ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                        : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('debug')}
                  >
                    Debug
                  </button>
                </li>
              </ul>
            </div>
            {activeTab === 'workflow' ? (
              <>
                {/* Execution progress */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    {renderConnectionStatus()}
                    <div className="execution-progress flex-grow mx-4">
                      <div className="flex justify-between items-center mb-1">
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Progress</span>
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{calculateProgress(execution.phases)}%</span>
                      </div>
                      <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2.5`}>
                        <div
                          className={`${darkMode ? 'bg-blue-500' : 'bg-blue-600'} h-2.5 rounded-full`}
                          style={{ width: `${calculateProgress(execution.phases)}%` }}
                        />
                      </div>
                    </div>
                    
                    <button
                      className={`px-4 py-2 rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white flex items-center`}
                      onClick={() => {
                        setIsManuallyRefreshing(true);
                        handleRefreshArtifacts()
                          .then(() => {
                            setLastPollingTime(new Date());
                            console.log('[MANUAL-REFRESH] Refresh completed');
                          })
                          .finally(() => {
                            setIsManuallyRefreshing(false);
                          });
                      }}
                      disabled={isManuallyRefreshing}
                    >
                      {isManuallyRefreshing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                          </svg>
                          Refresh Data
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Execution status indicator removed - already shown in the sidebar */}
                </div>
          
                {/* Workflow visualization */}
                <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
                  <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Workflow Graph</h3>
                  {execution && (
                    <PhaseDependencyGraph
                      height="500px"
                      workflowId={execution.workflow_id}
                      executionId={executionId}
                      executionData={execution}
                      disablePolling={true}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Debug tab content */}
                <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
                  <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Debug Information</h3>

                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    WebSocket Debug
                  </h4>
                  <div className={`p-4 rounded-md ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="flex items-center mb-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        connectionStatus === 'Open' ? 'bg-green-500' :
                        connectionStatus === 'Connecting' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}></div>
                      <div>WebSocket Status: {connectionStatus}</div>
                    </div>

                    <div className="flex items-center mb-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        isRealTimeMode ? 'bg-green-500' : 'bg-gray-500'
                      }`}></div>
                      <div>Real-Time Mode: {isRealTimeMode ? 'Active' : 'Inactive'}</div>
                    </div>

                    <div className="flex items-center mb-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        lastMessage ? 'bg-green-500' : 'bg-gray-500'
                      }`}></div>
                      <div>Last Message: {lastMessage ? new Date().toLocaleTimeString() : 'None'}</div>
                    </div>

                    <div className="mt-4">
                      <button
                        className={`px-3 py-2 rounded ${
                          darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                        } text-white mr-2`}
                        onClick={() => {
                          if (connectionStatus === 'Open') {
                            sendCommand('ping');
                            console.log('Sent ping command');
                          } else {
                            reconnect();
                            console.log('Attempting to reconnect WebSocket');
                          }
                        }}
                      >
                        {connectionStatus === 'Open' ? 'Send Ping' : 'Reconnect'}
                      </button>

                      <button
                        className={`px-3 py-2 rounded ${
                          darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                        } text-white`}
                        onClick={() => {
                          if (fetchExecutionRef.current) {
                            fetchExecutionRef.current();
                            console.log('Manually refreshed execution data');
                          }
                        }}
                      >
                        Force Refresh
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Execution ID: <span className="font-mono">{executionId}</span>
                  </h4>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Status: {execution?.status || 'Unknown'}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Raw Workflow Definition JSON
                  </h4>
                  <div className={`relative ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-md p-1`}>
                    <button
                      className={`absolute top-2 right-2 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white`}
                      onClick={() => {
                        setIsLoadingDefinition(true);
                        sessionService.getWorkflow(execution.workflow_id)
                          .then(data => {
                            setWorkflowDefinition(data);
                          })
                          .catch(err => {
                            console.error("Failed to load workflow definition:", err);
                            console.log("Failed to load workflow definition");
                            // Consider using a toast notification here
                          })
                          .finally(() => {
                            setIsLoadingDefinition(false);
                          });
                      }}
                      disabled={isLoadingDefinition}
                    >
                      {isLoadingDefinition ? 'Loading...' : 'Load Definition'}
                    </button>
                    <pre className={`overflow-auto p-4 max-h-96 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                      {workflowDefinition
                        ? JSON.stringify(workflowDefinition, null, 2)
                        : "Click 'Load Definition' to view the workflow JSON"}
                    </pre>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Connection Analysis
                  </h4>
                  <div className={`${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-md p-4`}>
                    {workflowDefinition ? (
                      <div>
                        <h5 className="font-medium mb-2">Persona Connections</h5>
                        {workflowDefinition.connections
                          .filter(conn => conn.dependencyType === 'persona_assignment')
                          .length > 0 ? (
                          <ul className="list-disc pl-5">
                            {workflowDefinition.connections
                              .filter(conn => conn.dependencyType === 'persona_assignment')
                              .map((conn, idx) => (
                                <li key={idx} className="mb-1">
                                  Persona <span className="font-mono">{conn.source}</span> →
                                  Phase <span className="font-mono">{conn.target}</span>
                                  <div className="text-xs mt-1">
                                    sourceHandle: <span className="font-mono">{conn.sourceHandle}</span>,
                                    targetHandle: <span className="font-mono">{conn.targetHandle}</span>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                            No persona assignment connections found in the workflow definition.
                          </p>
                        )}

                        <h5 className="font-medium mt-4 mb-2">Tool Connections</h5>
                        {workflowDefinition.connections
                          .filter(conn => conn.dependencyType === 'tool_assignment')
                          .length > 0 ? (
                          <ul className="list-disc pl-5">
                            {workflowDefinition.connections
                              .filter(conn => conn.dependencyType === 'tool_assignment')
                              .map((conn, idx) => (
                                <li key={idx} className="mb-1">
                                  Tool <span className="font-mono">{conn.source}</span> →
                                  Phase <span className="font-mono">{conn.target}</span>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className="text-sm">No tool assignment connections found.</p>
                        )}

                        <h5 className="font-medium mt-4 mb-2">Phase Flow Connections</h5>
                        {workflowDefinition.connections
                          .filter(conn => conn.dependencyType === 'flow')
                          .length > 0 ? (
                          <ul className="list-disc pl-5">
                            {workflowDefinition.connections
                              .filter(conn => conn.dependencyType === 'flow')
                              .map((conn, idx) => (
                                <li key={idx}>
                                  Phase <span className="font-mono">{conn.source}</span> →
                                  Phase <span className="font-mono">{conn.target}</span>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className="text-sm">No flow connections between phases found.</p>
                        )}
                      </div>
                    ) : (
                      <p>Load the workflow definition to analyze connections</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Raw Execution JSON
                  </h4>
                  <div className={`relative ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-md p-1`}>
                    <button
                      className={`absolute top-2 right-2 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white`}
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(execution, null, 2));
                        console.log('Execution JSON copied to clipboard');
                        // Use a toast notification if available in your UI library
                      }}
                    >
                      Copy
                    </button>
                    <pre className={`overflow-auto p-4 max-h-96 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                      {JSON.stringify(execution, null, 2)}
                    </pre>
                  </div>
                </div>

                {/* Persona Assignment Comparison */}
                {workflowDefinition && (
                  <div className="mb-4">
                    <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Persona Assignment Comparison
                    </h4>
                    <div className={`${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-md p-4`}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium mb-2">In Workflow Definition</h5>
                          {workflowDefinition.connections
                            .filter(conn => conn.dependencyType === 'persona_assignment')
                            .length > 0 ? (
                            <div>
                              {workflowDefinition.connections
                                .filter(conn => conn.dependencyType === 'persona_assignment')
                                .map((conn, idx) => {
                                  // Find the persona node to get the persona ID
                                  const personaNode = workflowDefinition.nodes?.find(n => n.id === conn.source);
                                  const personaId = personaNode?.data?.personaId || 'unknown';

                                  return (
                                    <div key={idx} className={`p-2 mb-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                      <div><strong>Persona:</strong> {personaId}</div>
                                      <div><strong>Phase:</strong> {conn.target}</div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                              No persona assignments in workflow definition
                            </p>
                          )}
                        </div>

                        <div>
                          <h5 className="font-medium mb-2">In Execution</h5>
                          {execution.phases.some(p => p.config?.personas?.length > 0) ? (
                            <div>
                              {execution.phases.map((phase, phaseIdx) => (
                                <div key={phaseIdx}>
                                  {phase.config?.personas?.length > 0 ? (
                                    phase.config.personas.map((persona, personaIdx) => (
                                      <div key={personaIdx} className={`p-2 mb-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                        <div><strong>Persona:</strong> {persona.persona_id}</div>
                                        <div><strong>Phase:</strong> {phase.id}</div>
                                        <div><strong>Role:</strong> {persona.role}</div>
                                      </div>
                                    ))
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                              No persona assignments in execution
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 p-3 border rounded border-yellow-500 bg-yellow-100 dark:bg-yellow-900 dark:border-yellow-700">
                        <h5 className="font-medium mb-1 text-yellow-800 dark:text-yellow-300">Potential Issues</h5>
                        <ul className="list-disc pl-5 text-sm text-yellow-800 dark:text-yellow-300">
                          {workflowDefinition.connections.filter(conn => conn.dependencyType === 'persona_assignment').length === 0 && (
                            <li>No persona assignment connections found in workflow definition</li>
                          )}
                          {execution.phases.every(p => !p.config?.personas?.length) && (
                            <li>No personas found in any phase configuration</li>
                          )}
                          {workflowDefinition.connections.filter(conn => conn.dependencyType === 'persona_assignment').length > 0 &&
                           execution.phases.every(p => !p.config?.personas?.length) && (
                            <li>Personas are defined in workflow but not transferred to execution</li>
                          )}
                          {workflowDefinition.connections.some(conn =>
                            conn.dependencyType === 'persona_assignment' &&
                            conn.sourceHandle !== 'persona_output'
                          ) && (
                            <li>Some persona connections have incorrect sourceHandle (should be 'persona_output')</li>
                          )}
                          {workflowDefinition.connections.some(conn =>
                            conn.dependencyType === 'persona_assignment' &&
                            conn.targetHandle !== 'personas'
                          ) && (
                            <li>Some persona connections have incorrect targetHandle (should be 'personas')</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase Details Section */}
                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phase Details
                  </h4>
                  <div className={`${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-md p-4`}>
                    <select
                      className={`w-full mb-4 p-2 border rounded ${
                        darkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
                      }`}
                      onChange={(e) => {
                        const selectedPhase = execution?.phases?.find(p => p.id === e.target.value);
                        if (selectedPhase) {
                          setSelectedPhaseId(selectedPhase.id);
                        }
                      }}
                      value={selectedPhaseId || ''}
                    >
                      <option value="">Select a phase...</option>
                      {execution?.phases?.map(phase => (
                        <option key={phase.id} value={phase.id}>
                          {phase.label} ({phase.status})
                        </option>
                      ))}
                    </select>

                    {selectedPhaseId && (
                      <div>
                        {execution?.phases?.filter(p => p.id === selectedPhaseId).map(phase => (
                          <div key={phase.id}>
                            <h5 className="font-medium mb-2">Phase Configuration</h5>
                            <div className={`relative ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-md p-1`}>
                              <pre className={`overflow-auto p-2 max-h-60 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                                {JSON.stringify(phase.config || {}, null, 2)}
                              </pre>
                            </div>

                            <h5 className="font-medium mt-4 mb-2">Assigned Personas</h5>
                            {phase.config?.personas?.length > 0 ? (
                              <div className="space-y-2">
                                {phase.config.personas.map((p, idx) => (
                                  <div key={idx} className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <div className="font-medium">{p.persona_id}</div>
                                    <div className="text-xs">Role: {p.role}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={`p-2 rounded ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'}`}>
                                No personas assigned to this phase
                              </div>
                            )}

                            {/* Manual persona assignment */}
                            <div className="mt-4">
                              <h5 className="font-medium mb-2">Add Persona Manually</h5>
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  placeholder="Persona ID (e.g., researcher)"
                                  className={`flex-1 p-2 border rounded ${
                                    darkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
                                  }`}
                                  value={manualPersonaId}
                                  onChange={(e) => setManualPersonaId(e.target.value)}
                                />
                                <button
                                  className={`px-3 py-2 rounded ${
                                    darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                                  } text-white`}
                                  onClick={() => {
                                    if (manualPersonaId && selectedPhaseId) {
                                      // Add persona to phase
                                      const updatedExecution = {...execution};
                                      const phaseIndex = updatedExecution.phases.findIndex(p => p.id === selectedPhaseId);

                                      if (phaseIndex >= 0) {
                                        if (!updatedExecution.phases[phaseIndex].config) {
                                          updatedExecution.phases[phaseIndex].config = {};
                                        }

                                        if (!updatedExecution.phases[phaseIndex].config.personas) {
                                          updatedExecution.phases[phaseIndex].config.personas = [];
                                        }

                                        updatedExecution.phases[phaseIndex].config.personas.push({
                                          persona_id: manualPersonaId,
                                          role: "Contributor"
                                        });

                                        // Update local state
                                        setExecution(updatedExecution);

                                        // Send update to server
                                        if (connectionStatus === 'Open') {
                                          sendCommand('update_phase_config', {
                                            phase_id: selectedPhaseId,
                                            config: updatedExecution.phases[phaseIndex].config
                                          });
                                        }

                                        setManualPersonaId('');
                                        console.log(`Added persona "${manualPersonaId}" to phase`);
                                        // Consider using a toast notification here
                                      }
                                    }
                                  }}
                                  disabled={!manualPersonaId}
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Connection Status */}
                <div className="mb-4">
                  <h4 className={`text-md font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Connection Status
                  </h4>
                  <div className={`p-4 rounded-md ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="flex items-center mb-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        connectionStatus === 'Open' ? 'bg-green-500' :
                        connectionStatus === 'Connecting' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}></div>
                      <div>WebSocket: {connectionStatus}</div>
                    </div>

                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        pollingIntervalRef.current ? 'bg-blue-500' : 'bg-gray-500'
                      }`}></div>
                      <div>Polling: {pollingIntervalRef.current ? 'Active' : 'Inactive'}</div>
                    </div>

                    <div className="mt-4">
                      <button
                        className={`px-3 py-2 rounded ${
                          darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                        } text-white mr-2`}
                        onClick={() => {
                          if (connectionStatus === 'Open') {
                            sendCommand('request_execution_state');
                            console.log('Requested latest execution state');
                            // Consider using a toast notification here
                          } else {
                            alert('WebSocket not connected. Cannot send command.');
                          }
                        }}
                      >
                        Request Latest State
                      </button>

                      <button
                        className={`px-3 py-2 rounded ${
                          darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                        } text-white`}
                        onClick={() => {
                          if (fetchExecutionRef.current) {
                            const now = Date.now();
                            if (now - lastRefreshTimeRef.current >= REFRESH_COOLDOWN) {
                              console.log('Manually refreshing execution data');
                              lastRefreshTimeRef.current = now;
                              fetchExecutionRef.current();
                            } else {
                              console.log(`Skipping manual refresh - within cooldown period (${now - lastRefreshTimeRef.current}ms < ${REFRESH_COOLDOWN}ms)`);
                              // Consider using a toast notification here
                            }
                          }
                        }}
                      >
                        Refresh Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Add Committee Execution Visualizer */}
          {currentPhase && currentPhase.status === 'in_progress' && (
            <div className={`rounded-lg shadow-md p-4 mt-6 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
              <CommitteeExecutionVisualizer
                committeeMode={committeeMode}
                personas={committeeState.personas.length > 0 ? committeeState.personas : (currentPhase.config?.personas || []).map((p: any) => ({
                  persona_id: p.persona_id,
                  role: p.role,
                  status: 'waiting'
                }))}
                currentPersonaIndex={committeeState.currentPersonaIndex}
                iteration={committeeState.iteration}
                totalIterations={committeeState.totalIterations}
              />
            </div>
          )}

          {/* Add Intermediate Artifacts Viewer */}
          <div className={`rounded-lg shadow-md p-4 mt-6 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
            <IntermediateArtifactsViewer
              artifacts={artifacts}
              personas={currentPhase?.config?.personas || []}
              onRefresh={handleRefreshArtifacts}
            />
          </div>

          {/* Agent activities section - use the enhanced AgentActivityTimeline component */}
          <div className={`rounded-lg shadow-md p-4 mt-6 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
            {agentActivities.length > 0 ? (
              <AgentActivityTimeline 
                activities={agentActivities} 
                getPersonaName={getPersonaName}
              />
            ) : (
              <div>
                <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Agent Activities</h3>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No agent activities recorded yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          {/* Committee Mode Selector */}
          <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Committee Settings</h3>

            <CommitteeModeSelector
              value={committeeMode}
              onChange={setCommitteeMode}
              disabled={loading}
            />
          </div>

          {/* Input Review Modal */}
          {showInputReviewModal && phaseInputs && (
            <InputReviewModal
              phaseInputs={phaseInputs}
              onClose={() => setShowInputReviewModal(false)}
              onRunPhase={runPhaseAfterReview}
              getPersonaName={getPersonaName}
            />
          )}

          {/* Execution status */}
          <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Execution Status</h3>
            <div className={`p-3 rounded-lg ${
              darkMode ? (
                execution?.status === 'active' ? 'bg-blue-900 text-blue-200' :
                execution?.status === 'paused' ? 'bg-yellow-900 text-yellow-200' :
                execution?.status === 'completed' ? 'bg-green-900 text-green-200' :
                execution?.status === 'failed' ? 'bg-red-900 text-red-200' :
                'bg-gray-700 text-gray-200'
              ) : (
                execution?.status === 'active' ? 'bg-blue-100 text-blue-800' :
                execution?.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                execution?.status === 'completed' ? 'bg-green-100 text-green-800' :
                execution?.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              )
            }`}>
              {execution?.status ? (execution.status.charAt(0).toUpperCase() + execution.status.slice(1)) : 'Unknown'}
            </div>

            {/* Execution controls */}
            <div className="mt-4 space-y-2">
              <button
                className={`w-full px-3 py-2 rounded ${
                  execution.status === 'active'
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600'
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
                } ${execution.status === 'completed' ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => {
                  // Update local state immediately for better UX
                  setExecution(prev => ({
                    ...prev,
                    status: prev.status === 'active' ? 'paused' : 'active'
                  }));

                  // Then send the command to the server
                  if (execution.status === 'active') {
                    sendCommand('pause_execution');
                  } else {
                    sendCommand('resume_execution');
                  }
                }}
                disabled={execution.status === 'completed'}
              >
                {execution.status === 'active' ? 'Pause Execution' : 'Resume Execution'}
              </button>
            </div>
          </div>

          {/* Active phase details */}
          {currentPhase && (
            <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Active Phase</h3>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
                <div className="font-medium">{currentPhase.label}</div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  Type: {currentPhase.type || 'Standard'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  Status: {currentPhase.status ? (currentPhase.status.charAt(0).toUpperCase() + currentPhase.status.slice(1)) : 'Unknown'}
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  Started: {currentPhase.start_time ? new Date(currentPhase.start_time).toLocaleTimeString() : 'Not started'}
                </div>

                {/* Phase completion button */}
                {currentPhase.status === 'in_progress' && (
                  <button
                    className={`mt-3 w-full px-3 py-2 bg-red-600 text-white rounded opacity-50 cursor-not-allowed dark:bg-red-700`}
                    disabled={true}
                  >
                    Cancel Phase
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Assigned personas */}
          {currentPhase && currentPhase.config?.personas?.length > 0 && (
            <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Active Personas</h3>
              <div className="space-y-2">
                {currentPhase.config.personas.map((assignment: any, index: number) => {
                  const persona = personas[assignment.persona_id];
                  return (
                    <div key={index} className={`p-2 rounded ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} flex items-center justify-center text-white`}>
                          {persona?.name?.charAt(0) || '?'}
                        </div>
                        <div className="ml-2">
                          <div className="font-medium">{persona?.name || assignment.persona_id}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role: {assignment.role}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phase list */}
          <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800 shadow-gray-900' : 'bg-white shadow-gray-200'}`}>
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>All Phases</h3>
            <div className="space-y-2">
              {execution.phases.map((phase: any) => (
                <div
                  key={phase.id}
                  className={`p-2 rounded-lg border ${
                    phase.id === execution.current_phase_id
                      ? darkMode ? 'bg-blue-900 border-blue-700 text-blue-100' : 'bg-blue-50 border-blue-200 text-blue-900'
                      : darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'
                  }`}
                >
                  <div className="flex items-center">
                    {/* Use the PhaseStatusIndicator component */}
                    <PhaseStatusIndicator
                      status={phase.status || 'not_started'}
                      showLabel={false}
                    />
                    <div className="ml-2 font-medium">{phase.label}</div>
                    <div className={`ml-auto text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {phase.status ? (phase.status.charAt(0).toUpperCase() + phase.status.slice(1)) : 'Not Started'}
                    </div>
                  </div>

                  {/* Add Review & Run button for not_started phases */}
                  {phase.status === 'not_started' && (
                    <button
                      className="mt-2 w-full px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
                      onClick={() => handleReviewPhaseInputs(phase.id)}
                      disabled={loading}
                    >
                      Review & Run
                    </button>
                  )}

                  {/* Keep the Complete button for in_progress phases */}
                  {phase.status === 'in_progress' && (
                    <button
                      className="mt-2 w-full px-2 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                      onClick={() => handleCompletePhase(phase.id)}
                    >
                      Complete
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default WorkflowExecution;
